import { Analytics } from "@vercel/analytics/react";
import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addMonths, addDays, subMonths, isToday, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Calendar, Ship, Users, Euro, LogOut, Menu, X, Check, Clock, Mail, Phone, User, 
  ChevronLeft, ChevronRight, Home, Plus, Trash2, Edit, AlertCircle, Lock,
  Search, Filter, MapPin, Tag, MoreVertical, CheckCircle, XCircle, 
  CalendarDays, List, Settings, Bell, CreditCard, TrendingUp, TrendingDown,
  Sun, Anchor, ChevronDown, Eye, FileText, RefreshCw, Download, Copy, ExternalLink,
  Waves, Wind, CloudLightning, AlertTriangle
} from "lucide-react";
import LandingPage from "./LandingPage";
import PrivacyPage from "./PrivacyPage";
import DeleteAccountPage from "./DeleteAccountPage";
import AcceptInvitePage from "./AcceptInvitePage";
import { PricingPage, AccountPage } from "./SubscriptionPages";
import { RegisterPage, OnboardingPage } from "./Onboarding";
import { SuperAdminLayout, SADashboardPage, SAAziendePage, SARevenuePage, SANotifichePage, SAInsightsPage, SASupportoPage, SARimborsiPage, SACompanyDetailPage, SAMarketingPage, SAUtentiPage, SACouponsPage, SAWhatsAppPage, SALeadsPage } from "./SuperAdmin";
import LeadPage from "./LeadPage";
import { LanguageProvider, usePageT } from "./i18n/LanguageContext";
import adminTranslations from "./i18n/adminTranslations";
import { CommandPalette } from "./components/CommandPalette";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { StickyHelpButton } from "./components/StickyHelpButton";
import { CustomCursor } from "./components/visual/CustomCursor";
import { CountUp } from "./components/visual/CountUp";
import { TrialBanner } from "./components/TrialBanner";
import { usePageMeta } from "./lib/usePageMeta";
import { Toaster as SonnerToaster } from "sonner";

// API Configuration - Points to api.nautiqo.cloud (Railway)
const API_URL = process.env.REACT_APP_API_URL;
const API = `${API_URL}/api`;
const LOGO_URL = "https://nautiqo.cloud/logo-nautiqo.png";

// Auth Context - Using Bearer token instead of cookies due to CORS
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('nautiqo_token') || sessionStorage.getItem('nautiqo_token'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const getAuthHeaders = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      setUser(data);
    } catch {
      setUser(false);
      setToken(null);
      localStorage.removeItem('nautiqo_token');
      sessionStorage.removeItem('nautiqo_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = true) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Check if backend returns token in response
    const authToken = data.token || data.access_token;
    if (authToken) {
      setToken(authToken);
      if (rememberMe) {
        localStorage.setItem('nautiqo_token', authToken);
        sessionStorage.removeItem('nautiqo_token');
      } else {
        sessionStorage.setItem('nautiqo_token', authToken);
        localStorage.removeItem('nautiqo_token');
      }
    }
    setUser(data.user || data);
    return data;
  };

  const register = async (registrationData) => {
    const { data } = await axios.post(`${API}/auth/register`, registrationData);
    const authToken = data.token || data.access_token;
    if (authToken) {
      setToken(authToken);
      localStorage.setItem('nautiqo_token', authToken);
      sessionStorage.removeItem('nautiqo_token');
    }
    setUser(data.user || data);
    if (typeof fbq !== 'undefined') fbq('track', 'CompleteRegistration');
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch {}
    setUser(false);
    setToken(null);
    localStorage.removeItem('nautiqo_token');
    sessionStorage.removeItem('nautiqo_token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/admin" replace />;
  return children;
};

// Deep-link entry point from mobile companion app.
// /account -> /admin/account if logged, else /login (preserves query string).
const AccountDeepLinkRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to={`/login${location.search}`} replace />;
  if (user.role === 'super_admin') return <Navigate to="/super-admin" replace />;
  return <Navigate to="/admin/account" replace />;
};

// Stripe redirects to /subscription?checkout=success|cancelled
// We forward to /admin/account preserving query params
const SubscriptionRedirect = () => {
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  const qs = searchParams.toString();
  if (!user) return <Navigate to={`/login`} replace />;
  return <Navigate to={`/admin/account${qs ? `?${qs}` : ''}`} replace />;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-400">Caricamento...</p>
    </div>
  </div>
);

const formatApiError = (detail) => {
  if (!detail) return "Errore sconosciuto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg).filter(Boolean).join(" ");
  return String(detail);
};

// ==================== DOWNLOAD REDIRECT ====================
const APP_STORE_URL = "https://apps.apple.com/app/id6762418993";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.nautiqo.app";
const LOGO_URL_DL = "https://nautiqo.cloud/logo-nautiqo.png";

const DownloadRedirect = () => {
  const t = usePageT(adminTranslations);
  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/iPad|iPhone|iPod/i.test(ua)) {
      window.location.replace(APP_STORE_URL);
    } else if (/Android/i.test(ua)) {
      window.location.replace(PLAY_STORE_URL);
    }
    // Desktop: mostra la pagina con entrambi i link
  }, []);

  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', background: '#061629', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
        <p style={{ color: '#94a3b8', fontSize: 14 }}>{t.redirecting}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#061629', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <img src={LOGO_URL_DL} alt="Nautiqo" style={{ width: 64, height: 64, borderRadius: '22%', marginBottom: 24 }} />
        <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>{t.downloadTitle}</h1>
        <p style={{ color: '#94a3b8', fontSize: 15, marginBottom: 32 }}>{t.downloadDesc}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', padding: '14px 24px', background: '#1d4ed8', color: '#fff', borderRadius: 12, fontWeight: 600, textDecoration: 'none', fontSize: 15 }}>
            {t.downloadIOS}
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', padding: '14px 24px', background: '#15803d', color: '#fff', borderRadius: 12, fontWeight: 600, textDecoration: 'none', fontSize: 15 }}>
            {t.downloadAndroid}
          </a>
        </div>
        <p style={{ color: '#475569', fontSize: 12, marginTop: 24 }}>
          <a href="/landing" style={{ color: '#475569' }}>{t.backToSite}</a>
        </p>
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const t = usePageT(adminTranslations);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password, rememberMe);
      // Redirect based on role
      const userData = result.user || result;
      if (userData?.role === 'super_admin') {
        navigate("/super-admin");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#061629] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Glow blobs */}
      <div className="absolute top-[-100px] right-[-80px] w-[320px] h-[320px] rounded-full bg-[#4A90D9]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-120px] left-[-80px] w-[280px] h-[280px] rounded-full bg-[#1B2A4A]/60 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-[22%] overflow-hidden shadow-xl shadow-black/40">
              <img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" />
            </div>
            <span className="text-white font-bold text-2xl tracking-tight">Nautiqo</span>
          </Link>
          <p className="text-slate-400">{t.loginSubtitle}</p>
        </div>

        {/* Card */}
        <form onSubmit={handleSubmit} className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl border border-white/10 shadow-2xl p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 flex items-center gap-3 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-4 rounded-xl bg-white/[0.07] border border-white/10 text-white placeholder-slate-600 focus:border-[#4A90D9] focus:ring-2 focus:ring-[#4A90D9]/20 outline-none transition text-sm"
                  placeholder={t.emailPlaceholder}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-4 rounded-xl bg-white/[0.07] border border-white/10 text-white placeholder-slate-600 focus:border-[#4A90D9] focus:ring-2 focus:ring-[#4A90D9]/20 outline-none transition text-sm"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              type="button"
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-5 h-5 rounded flex items-center justify-center border transition-all flex-shrink-0 ${rememberMe ? 'bg-[#4A90D9] border-[#4A90D9]' : 'bg-white/[0.07] border-white/20'}`}
              aria-label={t.rememberMe}
            >
              {rememberMe && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </button>
            <span
              className="text-slate-400 text-sm cursor-pointer select-none"
              onClick={() => setRememberMe(!rememberMe)}
            >
              {t.rememberMe}
            </span>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-8 bg-[#1B2A4A] hover:bg-[#243657] text-white py-4 rounded-xl font-semibold transition-all shadow-lg shadow-black/30 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t.signingIn}
              </>
            ) : t.signIn}
          </button>

          <div className="mt-6 text-center space-y-2">
            <p className="text-slate-500 text-sm">
              {t.noAccount}{' '}
              <Link to="/register" className="text-[#4A90D9] hover:text-[#6aaee8] font-semibold transition-colors" data-testid="goto-register">
                {t.registerFree}
              </Link>
            </p>
            <Link to="/" className="text-slate-600 hover:text-slate-400 text-sm transition-colors inline-block">
              {t.backToHome}
            </Link>
            <div className="flex justify-center pt-1">
              <LanguageSwitcher variant="light" />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== ADMIN LAYOUT ====================
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const t = usePageT(adminTranslations);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/admin", icon: Calendar, label: t.navCalendar },
    { path: "/admin/prenotazioni", icon: FileText, label: t.navBookings },
    { path: "/admin/barche", icon: Ship, label: t.navBoats },
    { path: "/admin/fatturato", icon: TrendingUp, label: t.navRevenue },
    { path: "/admin/impostazioni", icon: Settings, label: t.navSettings },
    { path: "/admin/account", icon: User, label: t.navAccount },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
      </div>
      {/* Mobile Header */}
      <div className="lg:hidden bg-white/10 backdrop-blur-sm/5 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center justify-between sticky top-0 z-40 relative">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
          <span className="font-semibold text-white">Nautiqo</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 backdrop-blur-sm/10 rounded-lg text-slate-400">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900/95 backdrop-blur-xl border-r border-white/10 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform z-50`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <div>
              <h1 className="font-semibold text-white">Nautiqo</h1>
              <p className="text-xs text-slate-500">{t.charterMgmt}</p>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === item.path 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:bg-white/10 backdrop-blur-sm/5 hover:text-slate-200'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-white truncate">{user?.first_name || user?.company_name || t.userFallback}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">{t.logout}</span>
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen relative z-10">
        {/* Top bar with cmd+K trigger */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-slate-900/40 backdrop-blur-md border-b border-white/5 px-6 py-3 items-center justify-end gap-3">
          <LanguageSwitcher variant="dark" />
          <CommandPalette user={user} onLogout={handleLogout} />
        </div>
        {/* Mobile-only command palette mounting (hidden trigger but keyboard active) */}
        <div className="lg:hidden">
          <CommandPalette user={user} onLogout={handleLogout} />
        </div>
        {children}
      </main>

      <StickyHelpButton />

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

// ==================== DASHBOARD PAGE ====================
// ==================== WEATHER WIDGET (dashboard) ====================
const WMO = (code, t) => {
  const c = Number(code);
  if (c === 0) return { e: '☀️', l: t?.weatherClear || 'Sereno' };
  if (c <= 2) return { e: '🌤️', l: t?.weatherPartlyCloudy || 'Poco nuvoloso' };
  if (c === 3) return { e: '☁️', l: t?.weatherCloudy || 'Nuvoloso' };
  if (c <= 48) return { e: '🌫️', l: t?.weatherFog || 'Nebbia' };
  if (c <= 55) return { e: '🌦️', l: t?.weatherDrizzle || 'Pioggerella' };
  if (c <= 65) return { e: '🌧️', l: t?.weatherRain || 'Pioggia' };
  if (c <= 75) return { e: '🌨️', l: t?.weatherSnow || 'Neve' };
  if (c <= 82) return { e: '🌦️', l: t?.weatherShowers || 'Rovesci' };
  if (c <= 86) return { e: '❄️', l: t?.weatherSnowWind || 'Neve e vento' };
  return { e: '⛈️', l: t?.weatherStorm || 'Temporale' };
};

// Extracts the geocodable city/place from a free-text departure port.
// "Porticciolo turistico di Marsala" -> "Marsala", "Porto di Palermo" -> "Palermo",
// "Marsala" -> "Marsala", "Porto Cervo" -> "Porto Cervo".
const stripPortPrefix = (p) => {
  const s = (p || '').trim();
  const m = s.match(/\bdi\s+(.+)$/i);
  return (m ? m[1] : s).trim();
};

const WeatherWidget = ({ boats }) => {
  const t = usePageT(adminTranslations);
  const cities = React.useMemo(() => {
    const set = [];
    (boats || []).forEach(b => {
      const c = stripPortPrefix(b.departure_port);
      if (c && !set.includes(c)) set.push(c);
    });
    return set;
  }, [boats]);

  const [city, setCity] = useState('');
  const [days, setDays] = useState([]);
  const [wLoading, setWLoading] = useState(false);
  const [wError, setWError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (cities.length && !city) setCity(cities[0]);
  }, [cities, city]);

  useEffect(() => {
    if (city) loadWeather(city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  const loadWeather = async (cityName) => {
    setWLoading(true); setWError(''); setDays([]);
    try {
      const geo = await axios.get(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityName)}&count=1&language=it&format=json`);
      const loc = geo.data?.results?.[0];
      if (!loc) { setWError(`Previsioni non disponibili per "${cityName}"`); setWLoading(false); return; }
      const { latitude, longitude } = loc;
      const [fc, marine] = await Promise.allSettled([
        axios.get(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max&wind_speed_unit=kmh&timezone=auto&forecast_days=7`),
        axios.get(`https://marine-api.open-meteo.com/v1/marine?latitude=${latitude}&longitude=${longitude}&daily=wave_height_max&timezone=auto&forecast_days=7`),
      ]);
      if (fc.status !== 'fulfilled') { setWError('Previsioni non disponibili'); setWLoading(false); return; }
      const d = fc.value.data.daily;
      const waves = marine.status === 'fulfilled' ? (marine.value.data?.daily?.wave_height_max || []) : [];
      const out = (d.time || []).map((t, i) => ({
        date: t,
        code: d.weather_code?.[i],
        tmax: d.temperature_2m_max?.[i],
        tmin: d.temperature_2m_min?.[i],
        wind: d.wind_speed_10m_max?.[i],
        wave: waves[i] ?? null,
      }));
      setDays(out);
    } catch (e) {
      setWError('Previsioni non disponibili');
    } finally {
      setWLoading(false);
    }
  };

  if (!cities.length) return null;

  const dayLabel = (dateStr, i) => {
    if (i === 0) return t.todayLabel;
    if (i === 1) return t.weatherTomorrow;
    try { return format(parseISO(dateStr), 'EEE', { locale: it }); } catch (e) { return ''; }
  };
  const windColor = (kmh) => kmh < 20 ? 'text-emerald-400' : kmh < 40 ? 'text-amber-400' : 'text-red-400';
  const waveColor = (m) => m < 0.5 ? 'text-emerald-400' : m < 1.5 ? 'text-amber-400' : 'text-red-400';

  return (
    <>
    {/* backdrop-blur on this card makes it the containing block for fixed descendants,
        so the 7-day modal must live outside it (sibling, below) */}
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-sm border border-white/10 overflow-hidden mb-6">
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-400" />
          <h2 className="font-semibold text-white">{t.weatherTitle} · {city}</h2>
        </div>
        <button onClick={() => loadWeather(city)} className="text-slate-400 hover:text-white transition p-1" title={t.weatherRefresh}>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {cities.length > 1 && (
        <div className="flex gap-2 px-5 pt-3 overflow-x-auto pb-1">
          {cities.map(c => (
            <button key={c} onClick={() => setCity(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${c === city ? 'bg-blue-600 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}>
              {c}
            </button>
          ))}
        </div>
      )}
      <div className="p-5">
        {wLoading ? (
          <p className="text-slate-400 text-sm text-center py-4">{t.weatherLoading}</p>
        ) : wError ? (
          <p className="text-slate-400 text-sm text-center py-4">{wError}</p>
        ) : days.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">{t.weatherUnavailable}</p>
        ) : (
          <button type="button" onClick={() => setExpanded(true)} className="w-full text-left cursor-pointer group" title={t.weather7Days} data-testid="weather-expand">
            <div className="grid grid-cols-3 gap-3">
              {days.slice(0, 3).map((day, i) => {
                const w = WMO(day.code, t);
                return (
                  <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10 group-hover:border-white/20 transition">
                    <p className="text-xs text-slate-400 font-medium mb-1 capitalize">{dayLabel(day.date, i)}</p>
                    <div className="text-2xl mb-1">{w.e}</div>
                    <p className="text-[11px] text-slate-400 mb-2 leading-tight">{w.l}</p>
                    <p className="text-sm font-semibold text-white">{Math.round(day.tmax)}° <span className="text-slate-500 font-normal">{Math.round(day.tmin)}°</span></p>
                    {day.wind != null && (
                      <div className={`flex items-center justify-center gap-1 mt-1.5 text-[11px] ${windColor(day.wind)}`}>
                        <Wind className="w-3 h-3" /> {Math.round(day.wind)} km/h
                      </div>
                    )}
                    {day.wave != null && (
                      <div className={`flex items-center justify-center gap-1 mt-1 text-[11px] ${waveColor(day.wave)}`}>
                        <Waves className="w-3 h-3" /> {day.wave}m
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs text-blue-400 font-medium mt-3 group-hover:text-blue-300 transition">{t.weatherFullWeek}</p>
          </button>
        )}
      </div>
    </div>

      {/* 7-day forecast modal */}
      {expanded && days.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setExpanded(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="weather-modal">
            <div className="p-5 border-b border-white/10 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center gap-2">
                <Sun className="w-5 h-5 text-amber-400" />
                <h3 className="font-semibold text-white">{t.weather7Days} · {city}</h3>
              </div>
              <button onClick={() => setExpanded(false)} className="text-slate-400 hover:text-white transition p-1" title={t.close}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3 divide-y divide-white/5">
              {days.map((day, i) => {
                const w = WMO(day.code, t);
                return (
                  <div key={i} className="flex items-center gap-3 px-2 py-3">
                    <div className="w-20 flex-shrink-0">
                      <p className="text-sm font-semibold text-white capitalize">{dayLabel(day.date, i)}</p>
                      <p className="text-[11px] text-slate-500">{(() => { try { return format(parseISO(day.date), 'd MMM', { locale: it }); } catch (e) { return ''; } })()}</p>
                    </div>
                    <div className="text-2xl w-9 text-center flex-shrink-0">{w.e}</div>
                    <p className="flex-1 text-xs text-slate-400 leading-tight">{w.l}</p>
                    <p className="text-sm font-semibold text-white w-16 text-right flex-shrink-0">{Math.round(day.tmax)}° <span className="text-slate-500 font-normal">{Math.round(day.tmin)}°</span></p>
                    <div className="w-20 flex-shrink-0">
                      {day.wind != null && (
                        <p className={`flex items-center justify-end gap-1 text-[11px] ${windColor(day.wind)}`}>
                          <Wind className="w-3 h-3" /> {Math.round(day.wind)} km/h
                        </p>
                      )}
                      {day.wave != null && (
                        <p className={`flex items-center justify-end gap-1 mt-1 text-[11px] ${waveColor(day.wave)}`}>
                          <Waves className="w-3 h-3" /> {day.wave}m
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== BOOKING DETAIL MODAL ====================
const bdTodayStr = () => new Date().toISOString().slice(0, 10);

const BookingDetailModal = ({ booking, onClose, onUpdated, onDeleted }) => {
  const { getAuthHeaders, user } = useAuth();
  const t = usePageT(adminTranslations);
  const BD_TIME_SLOT = { full_day: t.fullDay, morning: t.morning, afternoon: t.afternoon, sunset: t.sunset };
  const BD_BOOKING_STATUS = [
    { value: 'pending', label: t.statusPending },
    { value: 'accepted', label: t.statusAccepted },
    { value: 'awaiting_payment', label: t.statusAwaitingPayment },
    { value: 'confirmed', label: t.statusConfirmed },
    { value: 'completed', label: t.statusCompleted },
    { value: 'cancelled', label: t.statusCancelled },
  ];
  const BD_PAYMENT_STATUS = [
    { value: 'unpaid', label: t.payUnpaid, color: 'text-red-400' },
    { value: 'partial_deposit', label: t.payPartialDeposit, color: 'text-amber-400' },
    { value: 'deposit_received', label: t.payDepositReceived, color: 'text-amber-400' },
    { value: 'fully_paid', label: t.payFullyPaid, color: 'text-emerald-400' },
    { value: 'refunded', label: t.payRefunded, color: 'text-slate-400' },
  ];
  const BD_PM_LABELS = { contanti: t.pmCash, bonifico: t.pmBankTransfer, bancomat: t.pmDebitCard, carta: t.pmCard, cash: t.pmCash, card: t.pmCard, bank_transfer: t.pmBankTransfer, stripe: t.pmStripe, payment_link: t.pmLink };
  const [b, setB] = useState(booking);
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [payStatusOpen, setPayStatusOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmDelPayment, setConfirmDelPayment] = useState(null);
  const [pForm, setPForm] = useState({ amount: '', payment_method: 'contanti', payment_date: bdTodayStr(), payment_type: 'acconto', notes: '' });
  const [editForm, setEditForm] = useState({
    guest_count: booking.guest_count || 1,
    total_price: String(booking.total_price ?? ''),
    deposit_required: String(booking.deposit_required ?? ''),
    booking_date: booking.booking_date || '',
    internal_notes: booking.internal_notes || '',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPayments(); }, [booking?.id]);

  const headers = () => getAuthHeaders();

  const loadPayments = async () => {
    try {
      const res = await axios.get(`${API}/payments?booking_id=${booking.id}`, { headers: headers() });
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* ignore */ }
  };

  const refreshBooking = async () => {
    try {
      const res = await axios.get(`${API}/bookings/${booking.id}`, { headers: headers() });
      // Merge: GET /bookings/{id} non include customer_name/boat_name/customer_phone
      // (campi denormalizzati assenti dal modello Booking) — preserviamo quelli locali
      setB(prev => {
        const merged = { ...prev, ...res.data };
        onUpdated?.(merged);
        return merged;
      });
    } catch (e) { /* ignore */ }
  };

  const patch = async (data) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/bookings/${booking.id}`, data, { headers: headers() });
      setB(prev => {
        const merged = { ...prev, ...res.data };
        onUpdated?.(merged);
        return merged;
      });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail === 'PERMISSION_DENIED' ? t.noPermission : t.saveError);
    }
    finally { setBusy(false); }
  };

  const addPayment = async () => {
    const amount = parseFloat(String(pForm.amount).replace(',', '.'));
    if (isNaN(amount) || amount <= 0) { alert(t.invalidAmount); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/payments`, {
        booking_id: booking.id,
        amount,
        payment_method: pForm.payment_method,
        payment_date: pForm.payment_date || bdTodayStr(),
        payment_type: pForm.payment_type,
        notes: pForm.notes || undefined,
      }, { headers: headers() });
      setShowPay(false);
      setPForm({ amount: '', payment_method: 'contanti', payment_date: bdTodayStr(), payment_type: 'acconto', notes: '' });
      await loadPayments();
      await refreshBooking();
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    const count = parseInt(editForm.guest_count);
    const total = parseFloat(String(editForm.total_price).replace(',', '.'));
    const deposit = parseFloat(String(editForm.deposit_required).replace(',', '.'));
    if (isNaN(count) || count < 1) { alert(t.invalidGuests); return; }
    if (isNaN(total) || total < 0) { alert(t.invalidPrice); return; }
    if (isNaN(deposit) || deposit < 0) { alert(t.invalidDeposit); return; }
    if (deposit > total) { alert(t.depositExceedsTotal); return; }
    await patch({ guest_count: count, total_price: total, deposit_required: deposit, booking_date: editForm.booking_date, internal_notes: editForm.internal_notes });
    setShowEdit(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/bookings/${booking.id}`, { headers: headers() });
      onDeleted?.(booking.id);
      onClose();
    } catch (e) { console.error(e); setBusy(false); }
  };

  const deletePayment = async (paymentId) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/payments/${paymentId}`, { headers: headers() });
      await loadPayments();
      await refreshBooking();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || t.deletePaymentError);
    }
    finally { setBusy(false); setConfirmDelPayment(null); }
  };

  const canDelete = ['admin', 'company', 'super_admin'].includes(user?.role);
  const psMeta = BD_PAYMENT_STATUS.find(p => p.value === b.payment_status) || BD_PAYMENT_STATUS[0];
  const bsMeta = BD_BOOKING_STATUS.find(s => s.value === b.booking_status);
  const fmtDate = (d) => { try { return format(parseISO(d), 'd MMM yyyy', { locale: it }); } catch (e) { return d || '-'; } };
  const tsLabel = (s) => BD_TIME_SLOT[s] || s || '-';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-slate-900 border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white">{b.customer_name || t.guestFallback}</h2>
              {b.booking_code && <span className="text-xs font-mono bg-white/10 text-slate-400 px-1.5 py-0.5 rounded">{b.booking_code}</span>}
            </div>
            {b.customer_phone && <a href={`tel:${b.customer_phone}`} className="text-sm text-slate-400 hover:text-blue-400">{b.customer_phone}</a>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <button onClick={() => { setStatusOpen(o => !o); setPayStatusOpen(false); }} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white flex items-center gap-1">
                {bsMeta?.label || b.booking_status} <ChevronDown className="w-3 h-3" />
              </button>
              {statusOpen && (
                <div className="absolute left-0 top-full mt-1 bg-slate-800 border border-white/15 rounded-xl shadow-xl z-20 w-44 overflow-hidden">
                  {BD_BOOKING_STATUS.map(s => (
                    <button key={s.value} onClick={() => { patch({ booking_status: s.value }); setStatusOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 ${b.booking_status === s.value ? 'bg-white/5 text-white font-medium' : 'text-slate-300'}`}>{s.label}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button onClick={() => { setPayStatusOpen(o => !o); setStatusOpen(false); }} className={`text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 flex items-center gap-1 ${psMeta.color}`}>
                {psMeta.label} <ChevronDown className="w-3 h-3" />
              </button>
              {payStatusOpen && (
                <div className="absolute left-0 top-full mt-1 bg-slate-800 border border-white/15 rounded-xl shadow-xl z-20 w-44 overflow-hidden">
                  {BD_PAYMENT_STATUS.map(p => (
                    <button key={p.value} onClick={() => { patch({ payment_status: p.value }); setPayStatusOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 ${p.color} ${b.payment_status === p.value ? 'bg-white/5 font-medium' : ''}`}>{p.label}</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-slate-500">{t.fieldDate}</p><p className="text-white font-medium">{fmtDate(b.booking_date)}{b.end_date && b.end_date !== b.booking_date && <span className="text-blue-400"> → {fmtDate(b.end_date)}</span>}</p></div>
            <div><p className="text-slate-500">{t.fieldTimeSlot}</p><p className="text-white font-medium">{tsLabel(b.time_slot)}</p></div>
            <div><p className="text-slate-500">{t.fieldBoat}</p><p className="text-white font-medium">{b.boat_name || '-'}</p></div>
            <div><p className="text-slate-500">{t.fieldGuests}</p><p className="text-white font-medium">{b.guest_count || '-'}</p></div>
          </div>

          {(b.customer_notes || b.internal_notes) && (
            <div className="space-y-1 border-t border-white/10 pt-3">
              {b.customer_notes && <p className="text-xs text-slate-400"><span className="text-slate-500">{t.customerNotes}</span> {b.customer_notes}</p>}
              {b.internal_notes && <p className="text-xs text-slate-400"><span className="text-slate-500">{t.internalNotes}</span> {b.internal_notes}</p>}
            </div>
          )}

          <div className="border-t border-white/10 pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-white/5 rounded-xl p-3"><p className="text-xs text-slate-500">{t.fieldTotal}</p><p className="text-white font-bold">€{(b.total_price || 0).toLocaleString()}</p></div>
              <div className="bg-white/5 rounded-xl p-3"><p className="text-xs text-slate-500">{t.fieldDeposit}</p><p className="text-white font-bold">€{(b.deposit_required || 0).toLocaleString()}</p></div>
              <div className="bg-white/5 rounded-xl p-3"><p className="text-xs text-slate-500">{t.fieldCollected}</p><p className="text-emerald-400 font-bold">€{(b.deposit_received || 0).toLocaleString()}</p></div>
              <div className="bg-white/5 rounded-xl p-3"><p className="text-xs text-slate-500">{t.fieldRemaining}</p><p className="text-amber-400 font-bold">€{(b.remaining_balance || 0).toLocaleString()}</p></div>
            </div>

            <button onClick={() => setShowPay(s => !s)} className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center justify-center gap-2 mb-3">
              <Plus className="w-4 h-4" /> {t.addPayment}
            </button>

            {showPay && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3 mb-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">{t.paymentType}</label>
                    <select value={pForm.payment_type} onChange={e => setPForm(f => ({ ...f, payment_type: e.target.value, amount: e.target.value === 'saldo' ? String(b.remaining_balance || '') : f.amount }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="acconto">{t.paymentTypeDeposit}</option>
                      <option value="saldo">{t.paymentTypeBalance}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">{t.paymentAmount}</label>
                    <input type="text" inputMode="decimal" value={pForm.amount} readOnly={pForm.payment_type === 'saldo'} onChange={e => setPForm(f => ({ ...f, amount: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" placeholder="0" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">{t.paymentMethod}</label>
                    <select value={pForm.payment_method} onChange={e => setPForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="contanti">{t.pmCash}</option>
                      <option value="bonifico">{t.pmBankTransfer}</option>
                      <option value="bancomat">{t.pmDebitCard}</option>
                      <option value="carta">{t.pmCard}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">{t.csvDate}</label>
                    <input type="date" value={pForm.payment_date} onChange={e => setPForm(f => ({ ...f, payment_date: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                  </div>
                </div>
                <input type="text" value={pForm.notes} onChange={e => setPForm(f => ({ ...f, notes: e.target.value }))} placeholder={t.paymentNotes} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <button onClick={addPayment} disabled={busy} className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? '...' : t.savePayment}</button>
              </div>
            )}

            {payments.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium">{t.paymentHistory}</p>
                {payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm text-white font-medium">€{(p.amount || 0).toLocaleString()} <span className="text-xs text-slate-400">· {BD_PM_LABELS[p.payment_method] || p.payment_method}</span></p>
                      <p className="text-xs text-slate-500">{fmtDate(p.payment_date)} · {p.payment_type === 'balance' ? t.paymentTypeBalance : p.payment_type === 'deposit' ? t.paymentTypeDeposit : p.payment_type}</p>
                    </div>
                    {canDelete && (
                      confirmDelPayment === p.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => deletePayment(p.id)} disabled={busy} className="text-xs px-2 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">{busy ? '...' : t.delete}</button>
                          <button onClick={() => setConfirmDelPayment(null)} className="text-xs px-2 py-1 rounded-lg bg-white/10 text-slate-300">{t.cancel}</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDelPayment(p.id)} className="text-slate-500 hover:text-red-400 p-1" title={t.delete}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-4 space-y-3">
            <button onClick={() => setShowEdit(s => !s)} className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium flex items-center justify-center gap-2">
              <Edit className="w-4 h-4" /> {t.editBooking}
            </button>
            {showEdit && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 block mb-1">{t.fieldGuests}</label><input type="number" min="1" value={editForm.guest_count} onChange={e => setEditForm(f => ({ ...f, guest_count: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                  <div><label className="text-xs text-slate-400 block mb-1">{t.fieldDate}</label><input type="date" value={editForm.booking_date} onChange={e => setEditForm(f => ({ ...f, booking_date: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                  <div><label className="text-xs text-slate-400 block mb-1">{t.fieldPriceLabel}</label><input type="text" inputMode="decimal" value={editForm.total_price} onChange={e => setEditForm(f => ({ ...f, total_price: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                  <div><label className="text-xs text-slate-400 block mb-1">{t.fieldDepositLabel}</label><input type="text" inputMode="decimal" value={editForm.deposit_required} onChange={e => setEditForm(f => ({ ...f, deposit_required: e.target.value }))} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" /></div>
                </div>
                <textarea value={editForm.internal_notes} onChange={e => setEditForm(f => ({ ...f, internal_notes: e.target.value }))} placeholder={t.internalNotesPlaceholder} rows={2} className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                <button onClick={saveEdit} disabled={busy} className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">{busy ? '...' : t.saveChanges}</button>
              </div>
            )}
            {canDelete && (
              confirmDel ? (
                <div className="flex gap-2">
                  <button onClick={doDelete} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50">{busy ? '...' : t.confirmDelete}</button>
                  <button onClick={() => setConfirmDel(false)} className="px-4 py-2.5 rounded-xl bg-white/10 text-slate-300 text-sm">{t.cancel}</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDel(true)} className="w-full py-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-sm font-medium flex items-center justify-center gap-2">
                  <Trash2 className="w-4 h-4" /> {t.deleteBooking}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  usePageMeta({ title: "Dashboard · Nautiqo" });
  const t = usePageT(adminTranslations);
  const { user, getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingSlug, setBookingSlug] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const [dashboardRes, bookingsRes, boatsRes] = await Promise.all([
        axios.get(`${API}/dashboard`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/bookings`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/boats`, { headers }).catch(async () => {
          const pub = await axios.get(`${API}/public/boats`);
          return pub;
        })
      ]);
      // Fetch booking link slug
      axios.get(`${API}/booking-link`, { headers })
        .then(res => { if (res.data?.slug) setBookingSlug(res.data.slug); })
        .catch(() => {});
      setDashboard(dashboardRes.data);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setBoats(Array.isArray(boatsRes.data) ? boatsRes.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t.greetingMorning;
    if (hour < 18) return t.greetingAfternoon;
    return t.greetingEvening;
  };

  const todayBookings = bookings.filter(b => {
    const bookingDate = b.booking_date || b.date;
    return bookingDate && isSameDay(parseISO(bookingDate), selectedDate);
  });

  const pendingRequests = bookings.filter(b => b.booking_status === 'pending');
  const confirmedBookings = bookings.filter(b => b.booking_status === 'confirmed');
  const activeBoats = boats.filter(b => b.is_active !== false);

  const stats = {
    total_revenue: dashboard?.monthly_revenue || bookings.reduce((sum, b) => sum + (b.total_price || 0), 0),
    bookings_count: bookings.length,
    pending_count: dashboard?.pending_requests_count || pendingRequests.length,
    boats_count: dashboard?.active_boats || boats.length,
    deposits_total: dashboard?.monthly_deposits_collected || bookings.reduce((s, b) => s + (b.deposit_received || 0), 0),
    boats_available_today: dashboard?.boats_available_today || activeBoats.length,
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Header with greeting */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-slate-500 mb-1">
          <Sun className="w-5 h-5 text-amber-500" />
          <span className="text-sm">{getGreeting()}</span>
        </div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">
          {user?.company_name || user?.first_name || t.charterFallback}
        </h1>
      </div>

      {/* Trial / Expired / Past-due banner */}
      <TrialBanner />
      {(() => {
        const st = user?.subscription_status;
        const isExpired = ['expired', 'trial_expired', 'inactive'].includes(st);
        const isPastDue = st === 'past_due';

        if (isExpired) return (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="expired-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300"><span className="font-semibold text-red-200">{t.expiredTitle}</span> — {t.expiredMessage}</p>
            </div>
            <Link to="/pricing" className="px-4 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition flex-shrink-0">{t.activateNow}</Link>
          </div>
        );
        if (isPastDue) return (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="pastdue-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300"><span className="font-semibold text-red-200">{t.paymentIssueTitle}</span> — {t.paymentIssueMessage}</p>
            </div>
            <Link to="/admin/account" className="px-4 py-1.5 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 transition flex-shrink-0">{t.manage}</Link>
          </div>
        );
        return null;
      })()}

      {/* Date selector */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-6 shadow-sm border border-white/10">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSelectedDate(d => new Date(d.setDate(d.getDate() - 1)))}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="font-semibold text-white">
              {format(selectedDate, "EEEE d MMMM", { locale: it })}
            </p>
            {isToday(selectedDate) && (
              <span className="text-xs text-blue-600 font-medium">{t.todayLabel}</span>
            )}
          </div>
          <button 
            onClick={() => setSelectedDate(d => new Date(d.setDate(d.getDate() + 1)))}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Status badges */}
      {pendingRequests.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Link 
            to="/admin/prenotazioni"
            className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium whitespace-nowrap"
          >
            <Bell className="w-4 h-4" />
            {pendingRequests.length} {t.toConfirm}
          </Link>
          {bookings.filter(b => b.payment_status === 'unpaid').length > 0 && (
            <span className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium whitespace-nowrap">
              <CreditCard className="w-4 h-4" />
              {bookings.filter(b => b.payment_status === 'unpaid').length} {t.unpaidLabel}
            </span>
          )}
        </div>
      )}

      {/* Summary cards — KPI grid (responsive separators on mobile) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 divide-y sm:divide-y-0 divide-white/5 bg-white/5 sm:bg-transparent rounded-2xl sm:rounded-none border sm:border-0 border-white/10 overflow-hidden">
        {/* Today's bookings */}
        <div className="bg-transparent sm:bg-white/10 backdrop-blur-sm sm:rounded-2xl p-5 sm:shadow-sm sm:border sm:border-white/10 relative">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-500 text-sm font-medium">{t.bookingsToday}</span>
            <Calendar className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-white tabular-nums"><CountUp to={todayBookings.length} duration={800} /></p>
          <p className="text-sm text-slate-500 mt-1">
            {todayBookings.filter(b => b.booking_status === 'confirmed').length} {t.confirmedCount}
          </p>
        </div>

        {/* Fleet today */}
        <div className="bg-transparent sm:bg-white/10 backdrop-blur-sm sm:rounded-2xl p-5 sm:shadow-sm sm:border sm:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-500 text-sm font-medium">{t.fleetToday}</span>
            <Ship className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-white tabular-nums"><CountUp to={activeBoats.length} duration={800} /></p>
          <p className="text-sm text-slate-500 mt-1">
            <span className="text-emerald-600">{activeBoats.length - todayBookings.length} {t.available}</span>
            {' / '}
            <span className="text-amber-600">{Math.min(todayBookings.length, activeBoats.length)} {t.occupied}</span>
          </p>
        </div>

        {/* Pending requests */}
        <div className="bg-transparent sm:bg-white/10 backdrop-blur-sm sm:rounded-2xl p-5 sm:shadow-sm sm:border sm:border-white/10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-500 text-sm font-medium">{t.pendingRequests}</span>
            <Bell className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-3xl font-bold text-white tabular-nums"><CountUp to={pendingRequests.length} duration={800} /></p>
          <Link to="/admin/prenotazioni" className="text-sm text-blue-600 font-medium mt-1 inline-block">
            {t.viewAll}
          </Link>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link
          to="/admin/prenotazioni"
          className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-lg transition border border-white/10"
        >
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <span className="text-sm font-medium text-slate-300">{t.navBookings}</span>
        </Link>
        <Link
          to="/admin/fatturato"
          className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-lg transition border border-white/10"
        >
          <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-2">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
          </div>
          <span className="text-sm font-medium text-slate-300">{t.navRevenue}</span>
        </Link>
      </div>

      {/* Booking Link Widget */}
      {bookingSlug && (
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-white/10 mb-6" data-testid="booking-link-widget">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Anchor className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-semibold text-white text-sm">{t.bookingLink}</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-slate-800/50 rounded-xl px-4 py-3 text-sm text-slate-600 font-mono truncate border border-white/10" data-testid="booking-link-url">
              {window.location.origin}/booking/{bookingSlug}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/booking/${bookingSlug}`);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
              }}
              className={`flex items-center gap-1.5 px-4 py-3 rounded-xl text-sm font-medium transition whitespace-nowrap ${
                linkCopied
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-300 hover:bg-slate-200 border border-transparent'
              }`}
              data-testid="copy-booking-link"
            >
              {linkCopied ? <><Check className="w-4 h-4" /> {t.copied}</> : <><Copy className="w-4 h-4" /> {t.copy}</>}
            </button>
            <a
              href={`${window.location.origin}/booking/${bookingSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition whitespace-nowrap"
              data-testid="open-booking-link"
            >
              <ExternalLink className="w-4 h-4" /> {t.open}
            </a>
          </div>
        </div>
      )}

      {/* Weather (per boats' departure ports) */}
      <WeatherWidget boats={boats} />

      {/* Today's bookings list */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-sm border border-white/10 overflow-hidden">
        <div className="p-5 border-b border-white/10">
          <h2 className="font-semibold text-white">{t.todaysBookings}</h2>
        </div>
        {todayBookings.length === 0 ? (
          <div className="p-10 text-center">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">{t.noBookingsToday}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {(() => {
              const grouped = [];
              const usedIds = new Set();
              todayBookings.forEach(booking => {
                if (usedIds.has(booking.id)) return;
                if (booking.booking_type === 'tour_gruppo') {
                  const siblings = todayBookings.filter(b =>
                    b.booking_type === 'tour_gruppo' &&
                    b.boat_id === booking.boat_id &&
                    b.time_slot === booking.time_slot &&
                    !usedIds.has(b.id)
                  );
                  siblings.forEach(s => usedIds.add(s.id));
                  grouped.push({ type: 'group', bookings: siblings });
                } else {
                  usedIds.add(booking.id);
                  grouped.push({ type: 'single', booking });
                }
              });
              return grouped.map((item, gi) => {
                if (item.type === 'single') {
                  const booking = item.booking;
                  return (
              <div key={booking.id || gi} onClick={() => setDetailBooking(booking)} className="p-4 hover:bg-slate-800/50 transition cursor-pointer" data-testid={`today-booking-${gi}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      booking.booking_type === 'tour_esclusiva' ? 'bg-purple-50' :
                      booking.booking_type === 'locazione' ? 'bg-amber-50' : 'bg-blue-50'
                    }`}>
                      <Ship className={`w-5 h-5 ${
                        booking.booking_type === 'tour_esclusiva' ? 'text-purple-600' :
                        booking.booking_type === 'locazione' ? 'text-amber-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white">{booking.customer_name}</p>
                        {booking.booking_code && (
                          <span className="text-xs font-mono bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">{booking.booking_code}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <span>{booking.boat_name}</span>
                        <span className="text-slate-300">·</span>
                        <span>{booking.time_slot === 'full_day' ? t.fullDay : booking.time_slot === 'morning' ? t.morning : booking.time_slot === 'afternoon' ? t.afternoon : booking.time_slot === 'sunset' ? t.sunset : booking.time_slot || '-'}</span>
                        {booking.guest_count && (<><span className="text-slate-300">·</span><span>{booking.guest_count} {t.personsSuffix}</span></>)}
                      </div>
                      {booking.end_date && booking.end_date !== booking.booking_date && (
                        <p className="text-xs text-blue-600 font-medium mt-0.5">
                          {t.multiDayUntil} {format(parseISO(booking.end_date), 'd MMM', { locale: it })}
                          {booking.duration_slot && ` (${booking.duration_slot} ${Number(booking.duration_slot) === 1 ? t.dayLabel : t.daysLabel})`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-white">€{(booking.total_price || 0).toLocaleString()}</p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      booking.booking_status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>{booking.booking_status === 'confirmed' ? t.statusConfirmedBadge : t.statusPendingBadge}</span>
                  </div>
                </div>
              </div>
                  );
                }
                // Grouped tour_gruppo
                const gB = item.bookings;
                const first = gB[0];
                const tGuests = gB.reduce((s, b) => s + (b.guest_count || 0), 0);
                const tPrice = gB.reduce((s, b) => s + (b.total_price || 0), 0);
                return (
              <div key={`tg-${gi}`} className="p-4 bg-blue-50/40" data-testid={`today-group-${gi}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white">{t.groupTour}</p>
                        <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{gB.length} {t.bookingsSuffix}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <span>{first.boat_name}</span>
                        <span className="text-slate-300">·</span>
                        <span>{first.time_slot === 'full_day' ? t.fullDay : first.time_slot === 'morning' ? t.morning : first.time_slot === 'afternoon' ? t.afternoon : first.time_slot === 'sunset' ? t.sunset : first.time_slot || '-'}</span>
                        <span className="text-slate-300">·</span>
                        <span>{tGuests} {t.personsSuffix}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-white">€{tPrice.toLocaleString()}</p>
                  </div>
                </div>
                <div className="ml-13 pl-13 space-y-1 ml-[52px]">
                  {gB.map((b, bi) => (
                    <div key={bi} onClick={() => setDetailBooking(b)} className="flex items-center justify-between text-sm cursor-pointer hover:text-blue-400">
                      <span className="text-slate-600">{b.customer_name} <span className="text-slate-400">({b.guest_count} {t.personsSuffix})</span></span>
                      <span className="text-slate-300 font-medium">€{(b.total_price || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {detailBooking && (
        <BookingDetailModal
          booking={detailBooking}
          onClose={() => setDetailBooking(null)}
          onUpdated={(ub) => setBookings(bs => bs.map(x => x.id === ub.id ? { ...x, ...ub } : x))}
          onDeleted={(id) => { setBookings(bs => bs.filter(x => x.id !== id)); setDetailBooking(null); }}
        />
      )}
    </div>
  );
};

// ==================== PRENOTAZIONI PAGE ====================
const PrenotazioniPage = () => {
  usePageMeta({ title: "Prenotazioni · Nautiqo" });
  const t = usePageT(adminTranslations);
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [actionLoading, setActionLoading] = useState({}); // { [bookingId]: true }
  const [confirmCancel, setConfirmCancel] = useState(null); // booking to cancel
  const [paymentPickerId, setPaymentPickerId] = useState(null);
  const [bookingEditId, setBookingEditId] = useState(null);
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [detailBooking, setDetailBooking] = useState(null);
  const [bookingEditForm, setBookingEditForm] = useState({ guest_count: 1, total_price: '', deposit_required: '', booking_date: '', internal_notes: '' }); // booking id showing payment picker

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [bookingsRes, boatsRes, customersRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/boats`, { headers }).catch(() => axios.get(`${API}/public/boats`)),
        axios.get(`${API}/customers`, { headers }).catch(() => ({ data: [] }))
      ]);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setBoats(Array.isArray(boatsRes.data) ? boatsRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateBooking = async (bookingId, patch) => {
    setActionLoading(a => ({ ...a, [bookingId]: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.put(`${API}/bookings/${bookingId}`, patch, { headers });
      setBookings(bs => bs.map(b => b.id === bookingId ? { ...b, ...res.data } : b));
      return res.data;
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(a => ({ ...a, [bookingId]: false }));
    }
  };

  const handleCancelConfirmed = async () => {
    if (!confirmCancel) return;
    await updateBooking(confirmCancel.id, { booking_status: 'cancelled' });
    setConfirmCancel(null);
  };

  const openBookingEdit = (booking) => {
    setBookingEditForm({
      guest_count: booking.guest_count || 1,
      total_price: String(booking.total_price || ''),
      deposit_required: String(booking.deposit_required || ''),
      booking_date: booking.booking_date || '',
      internal_notes: booking.internal_notes || '',
    });
    setBookingEditId(booking.id);
  };

  const handleSaveBookingEdit = async () => {
    const count = parseInt(bookingEditForm.guest_count);
    if (isNaN(count) || count < 1) return;
    const total = parseFloat(String(bookingEditForm.total_price).replace(',', '.'));
    const deposit = parseFloat(String(bookingEditForm.deposit_required).replace(',', '.'));
    if (isNaN(total) || total < 0) return;
    if (isNaN(deposit) || deposit < 0) return;
    await updateBooking(bookingEditId, {
      guest_count: count,
      total_price: total,
      deposit_required: deposit,
      booking_date: bookingEditForm.booking_date,
      internal_notes: bookingEditForm.internal_notes,
    });
    setBookingEditId(null);
  };

  // Returns action buttons config based on booking status
  const getActions = (booking) => {
    const s = booking.booking_status;
    if (s === 'cancelled' || s === 'completed') return [];
    const actions = [];
    if (s === 'pending') {
      actions.push({ label: t.accept, color: 'emerald', patch: { booking_status: 'confirmed' } });
      actions.push({ label: t.reject, color: 'red', cancel: true });
    } else if (s === 'accepted') {
      actions.push({ label: t.confirm, color: 'blue', patch: { booking_status: 'confirmed' } });
      actions.push({ label: t.cancel, color: 'red', cancel: true });
    } else if (s === 'confirmed') {
      actions.push({ label: t.complete, color: 'slate', patch: { booking_status: 'completed' } });
      actions.push({ label: t.cancel, color: 'red', cancel: true });
    } else if (s === 'awaiting_payment') {
      actions.push({ label: t.confirm, color: 'blue', patch: { booking_status: 'confirmed' } });
      actions.push({ label: t.cancel, color: 'red', cancel: true });
    }
    return actions;
  };

  const PAYMENT_STATUSES = [
    { value: 'unpaid', label: t.payUnpaidShort, color: 'text-red-400' },
    { value: 'deposit_received', label: t.payDepositReceivedShort, color: 'text-amber-400' },
    { value: 'partial_deposit', label: t.payPartialDepositShort, color: 'text-amber-400' },
    { value: 'fully_paid', label: t.payPaid, color: 'text-emerald-400' },
    { value: 'refunded', label: t.payRefunded, color: 'text-slate-400' },
  ];

  const PAYMENT_METHODS_LABELS = {
    cash: t.pmCash, card: t.pmCard, bank_transfer: t.pmBankTransfer,
    payment_link: t.pmPaymentLink, other: t.pmOther,
    contanti: t.pmCash, carta: t.pmCard, bonifico: t.pmBankTransfer, stripe: t.pmStripe,
  };

  const BOOKING_SOURCE_LABELS = {
    manual: t.sourceManual, website: t.sourceWebsite, phone: t.sourcePhone,
    whatsapp: t.sourceWhatsApp, agency: t.sourceAgency, web: t.sourceWeb, app: t.sourceApp,
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingCount = bookings.filter(b => !(b.booking_date || '') || (b.booking_date || '').slice(0, 10) >= todayStr).length;
  const pastCount = bookings.filter(b => (b.booking_date || '') && (b.booking_date || '').slice(0, 10) < todayStr).length;
  let filteredBookings = [...bookings]
    .sort((a, b) => (a.booking_date || '').localeCompare(b.booking_date || ''))
    .filter(b => {
      const matchesSearch = !searchQuery ||
        (b.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.boat_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || b.booking_status === statusFilter;
      const bDate = (b.booking_date || '').slice(0, 10);
      let matchesTime = true;
      if (timeFilter === 'upcoming') matchesTime = !bDate || bDate >= todayStr;
      else if (timeFilter === 'past') matchesTime = !!bDate && bDate < todayStr;
      return matchesSearch && matchesStatus && matchesTime;
    });
  if (timeFilter === 'past') filteredBookings = filteredBookings.reverse();

  const getStatusBadge = (booking) => {
    const s = booking.booking_status;
    const p = booking.payment_status;
    if (s === 'cancelled') return <span className="px-2 py-1 bg-red-500/15 text-red-400 rounded-full text-xs font-medium">{t.statusCancelled}</span>;
    if (s === 'completed') return <span className="px-2 py-1 bg-slate-500/15 text-slate-400 rounded-full text-xs font-medium">{t.statusCompleted}</span>;
    if (s === 'pending') return <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs font-medium animate-pulse">{t.statusPending}</span>;
    if (s === 'awaiting_payment') return <span className="px-2 py-1 bg-orange-500/15 text-orange-400 rounded-full text-xs font-medium">{t.statusAwaitingPayment}</span>;
    if (s === 'accepted') return <span className="px-2 py-1 bg-sky-500/15 text-sky-400 rounded-full text-xs font-medium">{t.statusAccepted}</span>;
    if (s === 'confirmed' && p === 'fully_paid') return <span className="px-2 py-1 bg-emerald-500/15 text-emerald-400 rounded-full text-xs font-medium">{t.statusPaidBadge}</span>;
    if (s === 'confirmed' && p === 'deposit_received') return <span className="px-2 py-1 bg-blue-500/15 text-blue-400 rounded-full text-xs font-medium">{t.statusConfirmedDeposit}</span>;
    if (s === 'confirmed') return <span className="px-2 py-1 bg-blue-500/15 text-blue-400 rounded-full text-xs font-medium">{t.statusConfirmedBadge}</span>;
    return <span className="px-2 py-1 bg-slate-500/15 text-slate-400 rounded-full text-xs font-medium">{s}</span>;
  };

  // Calendar view helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();

  const getBookingsForDay = (day) => {
    return bookings.filter(b => {
      const bookingDate = b.booking_date || b.date;
      return bookingDate && isSameDay(parseISO(bookingDate), day);
    });
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">{t.navBookings}</h1>
        <Link to="/admin/nuova-prenotazione" className="bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 hover:bg-blue-700 transition text-sm">
          <Plus className="w-4 h-4" /> {t.newBookingTitle}
        </Link>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setViewMode('list')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <List className="w-4 h-4" />
          {t.listView}
        </button>
        <button
          onClick={() => setViewMode('calendar')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          {t.calendarView}
        </button>
      </div>

      {viewMode === 'list' && (
        <div className="flex items-center gap-2 mb-4">
          {[
            { id: 'upcoming', label: `${t.filterUpcoming}${upcomingCount ? ' · ' + upcomingCount : ''}` },
            { id: 'past', label: `${t.filterPast}${pastCount ? ' · ' + pastCount : ''}` },
            { id: 'all', label: t.filterAll },
          ].map(t => (
            <button key={t.id} onClick={() => setTimeFilter(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${timeFilter === t.id ? 'bg-blue-600 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl focus:border-blue-500 outline-none"
        >
          <option value="all">{t.filterAll}</option>
          <option value="confirmed">{t.statusConfirmedBadge}</option>
          <option value="pending">{t.statusPending}</option>
          <option value="cancelled">{t.statusCancelled}</option>
        </select>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {filteredBookings.length === 0 ? (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t.noBookingsFound}</p>
            </div>
          ) : (() => {
            // Group tour_gruppo bookings by date + boat + time_slot
            const grouped = [];
            const usedIds = new Set();
            filteredBookings.forEach(booking => {
              if (usedIds.has(booking.id)) return;
              if (booking.booking_type === 'tour_gruppo') {
                const siblings = filteredBookings.filter(b =>
                  b.booking_type === 'tour_gruppo' &&
                  b.booking_date === booking.booking_date &&
                  b.boat_id === booking.boat_id &&
                  b.time_slot === booking.time_slot &&
                  !usedIds.has(b.id)
                );
                siblings.forEach(s => usedIds.add(s.id));
                grouped.push({ type: 'group', bookings: siblings });
              } else {
                usedIds.add(booking.id);
                grouped.push({ type: 'single', booking });
              }
            });

            return grouped.map((item, gIdx) => {
              if (item.type === 'single') {
                const booking = item.booking;
                return (
              <div key={booking.id || gIdx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:shadow-lg transition" data-testid={`booking-card-${booking.id || gIdx}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="cursor-pointer" onClick={() => setDetailBooking(booking)}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white hover:text-blue-400 transition">{booking.customer_name || t.guestFallback}</h3>
                      {booking.booking_code && (
                        <span className="text-xs font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded" data-testid="booking-code">{booking.booking_code}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        booking.booking_type === 'tour_esclusiva' ? 'bg-purple-50 text-purple-700' :
                        booking.booking_type === 'locazione' ? 'bg-amber-50 text-amber-700' : 'bg-slate-800/50 text-slate-600'
                      }`}>
                        {booking.booking_type === 'tour_esclusiva' ? t.typeExclusive : booking.booking_type === 'locazione' ? t.typeRental : t.navBookings}
                      </span>
                      {booking.experience_type && (
                        <span className="text-xs bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full font-medium">{booking.experience_type === 'charter' ? 'Charter' : booking.experience_type}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {booking.booking_status !== 'cancelled' && (
                      <button
                        onClick={() => openBookingEdit(booking)}
                        className="text-slate-500 hover:text-blue-400 transition p-1"
                        title={t.editBooking}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        </svg>
                      </button>
                    )}
                    {getStatusBadge(booking)}
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">{t.fieldDate}</p>
                    <p className="font-medium text-white" data-testid="booking-date">
                      {booking.booking_date ? format(parseISO(booking.booking_date), 'd MMM yyyy', { locale: it }) : '-'}
                      {booking.end_date && booking.end_date !== booking.booking_date && (
                        <span className="text-blue-600"> → {format(parseISO(booking.end_date), 'd MMM yyyy', { locale: it })}</span>
                      )}
                    </p>
                    {booking.duration_slot && (
                      <p className="text-xs text-blue-600 font-medium mt-0.5">{booking.duration_slot} {Number(booking.duration_slot) === 1 ? t.dayLabel : t.daysLabel}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-slate-500">{t.fieldTimeSlot}</p>
                    <p className="font-medium text-white">
                      {booking.time_slot === 'full_day' ? t.fullDay : booking.time_slot === 'morning' ? t.morning : booking.time_slot === 'afternoon' ? t.afternoon : booking.time_slot === 'sunset' ? t.sunset : booking.time_slot || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">{t.fieldBoat}</p>
                    <p className="font-medium text-white">{booking.boat_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">{t.fieldGuests}</p>
                    <p className="font-medium text-white">{booking.guest_count || '-'}</p>
                  </div>
                </div>

                {/* Dettagli aggiuntivi */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mt-3">
                  {booking.departure_port && (
                    <div>
                      <p className="text-slate-500">{t.boatPort.replace(' *', '')}</p>
                      <p className="font-medium text-white flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{booking.departure_port}</p>
                    </div>
                  )}
                  {booking.payment_method && (
                    <div>
                      <p className="text-slate-500">{t.paymentMethodLabel}</p>
                      <p className="font-medium text-white">{PAYMENT_METHODS_LABELS[booking.payment_method] || booking.payment_method}</p>
                    </div>
                  )}
                  {booking.booking_source && (
                    <div>
                      <p className="text-slate-500">{t.bookingSourceLabel}</p>
                      <p className="font-medium text-white">{BOOKING_SOURCE_LABELS[booking.booking_source] || booking.booking_source}</p>
                    </div>
                  )}
                  {booking.deposit_required > 0 && (
                    <div>
                      <p className="text-slate-500">{t.fieldDeposit}</p>
                      <p className="font-medium text-white">
                        €{(booking.deposit_received || 0).toLocaleString()} / €{(booking.deposit_required || 0).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Extras */}
                {booking.extras && booking.extras.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {booking.extras.map((extra, ei) => (
                      <span key={ei} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{typeof extra === 'string' ? extra : extra.name || JSON.stringify(extra)}</span>
                    ))}
                  </div>
                )}

                {/* Note */}
                {(booking.customer_notes || booking.internal_notes) && (
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                    {booking.customer_notes && (
                      <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">{t.customerNotes}</span> {booking.customer_notes}</p>
                    )}
                    {booking.internal_notes && (
                      <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">{t.internalNotes}</span> {booking.internal_notes}</p>
                    )}
                  </div>
                )}

                {/* Price + payment status row */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <div>
                    <span className="text-lg font-bold text-white">€{(booking.total_price || 0).toLocaleString()}</span>
                    {booking.remaining_balance > 0 && booking.booking_status !== 'cancelled' && (
                      <span className="text-sm text-amber-400 ml-2">(€{booking.remaining_balance.toLocaleString()} {t.fieldRemaining})</span>
                    )}
                  </div>
                  {booking.booking_status !== 'cancelled' && (
                    <div className="relative">
                      <button
                        onClick={() => setPaymentPickerId(paymentPickerId === booking.id ? null : booking.id)}
                        className={`text-xs px-2.5 py-1.5 rounded-lg border transition flex items-center gap-1 ${
                          booking.payment_status === 'fully_paid' ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10' :
                          booking.payment_status === 'deposit_received' ? 'border-amber-500/30 text-amber-400 bg-amber-500/10' :
                          'border-white/10 text-slate-400 bg-slate-800/50'
                        }`}
                      >
                        {(PAYMENT_STATUSES.find(p => p.value === booking.payment_status) || PAYMENT_STATUSES[0]).label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {paymentPickerId === booking.id && (
                        <div className="absolute right-0 bottom-full mb-1 bg-slate-800 border border-white/15 rounded-xl shadow-xl z-20 overflow-hidden w-44">
                          {PAYMENT_STATUSES.map(ps => (
                            <button key={ps.value} onClick={() => { updateBooking(booking.id, { payment_status: ps.value }); setPaymentPickerId(null); }}
                              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 transition ${ps.color} ${booking.payment_status === ps.value ? 'bg-white/5 font-medium' : ''}`}>
                              {ps.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {(() => {
                  const acts = getActions(booking);
                  if (!acts.length) return null;
                  const isLoading = actionLoading[booking.id];
                  return (
                    <div className="flex gap-2 mt-3">
                      {acts.map(act => (
                        <button key={act.label} disabled={isLoading}
                          onClick={() => act.cancel ? setConfirmCancel(booking) : updateBooking(booking.id, act.patch)}
                          className={`flex-1 py-2 text-sm font-medium rounded-xl transition disabled:opacity-50 ${
                            act.color === 'emerald' ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30' :
                            act.color === 'blue' ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30' :
                            act.color === 'red' ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20' :
                            'bg-slate-700/50 text-slate-400 hover:bg-slate-700 border border-white/10'
                          }`}>
                          {isLoading ? '...' : act.label}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>
                );
              }

              // GROUP CARD for tour_gruppo
              const gBookings = item.bookings;
              const first = gBookings[0];
              const totalGuests = gBookings.reduce((s, b) => s + (b.guest_count || 0), 0);
              const totalPrice = gBookings.reduce((s, b) => s + (b.total_price || 0), 0);
              const totalRemaining = gBookings.reduce((s, b) => s + (b.remaining_balance || 0), 0);
              const totalDeposit = gBookings.reduce((s, b) => s + (b.deposit_received || 0), 0);
              const totalDepositReq = gBookings.reduce((s, b) => s + (b.deposit_required || 0), 0);

              return (
              <div key={`group-${gIdx}`} className="bg-white/10 backdrop-blur-sm rounded-2xl border-2 border-blue-200 hover:shadow-lg transition overflow-hidden" data-testid={`booking-group-${gIdx}`}>
                {/* Group header */}
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{t.groupTour}</h3>
                          <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{gBookings.length} {gBookings.length === 1 ? t.bookingsSuffix : t.bookingsSuffix}</span>
                        </div>
                        <p className="text-sm text-slate-500">
                          {first.boat_name || '-'} · {first.booking_date ? format(parseISO(first.booking_date), 'd MMM yyyy', { locale: it }) : '-'} · {first.time_slot === 'full_day' ? t.fullDay : first.time_slot === 'morning' ? t.morning : first.time_slot === 'afternoon' ? t.afternoon : first.time_slot === 'sunset' ? t.sunset : first.time_slot || '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">{totalGuests} {t.personsSuffix}</p>
                      <p className="text-lg font-bold text-white">€{totalPrice.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Individual bookings */}
                <div className="divide-y divide-slate-100">
                  {gBookings.map((b, bi) => (
                    <div key={b.id || bi} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold text-slate-500">
                            {(b.customer_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white text-sm">{b.customer_name || t.guestFallback}</span>
                              {b.booking_code && <span className="text-xs font-mono bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">{b.booking_code}</span>}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                              <span>{b.guest_count || 0} {t.personsSuffix}</span>
                              {b.payment_method && <><span className="text-slate-300">·</span><span>{PAYMENT_METHODS_LABELS[b.payment_method] || b.payment_method}</span></>}
                              {b.booking_source && <><span className="text-slate-300">·</span><span>{BOOKING_SOURCE_LABELS[b.booking_source] || b.booking_source}</span></>}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-white text-sm">€{(b.total_price || 0).toLocaleString()}</span>
                          {b.remaining_balance > 0 && (
                            <p className="text-xs text-amber-600">€{b.remaining_balance.toLocaleString()} {t.fieldRemaining}</p>
                          )}
                          {b.remaining_balance === 0 && b.total_price > 0 && (
                            <p className="text-xs text-emerald-600">{t.payPaid}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Group footer totals */}
                <div className="bg-slate-800/50 px-4 py-3 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    {totalDepositReq > 0 && (
                      <span className="text-slate-500">{t.fieldDeposit}: <span className="font-medium text-slate-300">€{totalDeposit.toLocaleString()} / €{totalDepositReq.toLocaleString()}</span></span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-white">{t.revenueTotal}: €{totalPrice.toLocaleString()}</span>
                    {totalRemaining > 0 && (
                      <span className="text-sm text-amber-600 ml-2">(€{totalRemaining.toLocaleString()} {t.fieldRemaining})</span>
                    )}
                  </div>
                </div>
              </div>
              );
            });
          })()}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="p-2 hover:bg-slate-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="font-semibold text-white capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </h3>
            <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="p-2 hover:bg-slate-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square"></div>
            ))}
            {days.map(day => {
              const dayBookings = getBookingsForDay(day);
              /* Also find multi-day bookings that span this day */
              const multiDayOnThisDay = bookings.filter(b => {
                if (!b.end_date || !b.booking_date || b.end_date === b.booking_date) return false;
                const start = parseISO(b.booking_date);
                const end = parseISO(b.end_date);
                return day > start && day <= end;
              });
              const allDayBookings = [...dayBookings, ...multiDayOnThisDay];
              const typeColorMap = {
                tour_gruppo: 'bg-blue-100 text-blue-700',
                tour_esclusiva: 'bg-purple-100 text-purple-700',
                locazione: 'bg-amber-100 text-amber-700',
              };
              return (
                <div
                  key={day.toISOString()}
                  className={`aspect-square p-1 rounded-lg border ${
                    isToday(day) ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:bg-slate-800/50'
                  }`}
                >
                  <div className="text-sm text-slate-300 mb-1">{format(day, 'd')}</div>
                  {allDayBookings.slice(0, 2).map((b, i) => (
                    <div key={i} className={`text-xs rounded px-1 py-0.5 truncate mb-0.5 ${typeColorMap[b.booking_type] || 'bg-blue-100 text-blue-700'}`}>
                      {b.boat_name || 'Prenotazione'}
                    </div>
                  ))}
                  {allDayBookings.length > 2 && (
                    <div className="text-xs text-slate-500">+{allDayBookings.length - 2}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{t.confirmDelete}</h3>
            <p className="text-slate-400 text-sm mb-1">{t.deleteBoatConfirm}</p>
            <p className="font-semibold text-white mb-1">{confirmCancel.customer_name}</p>
            <p className="text-slate-500 text-sm mb-6">
              {confirmCancel.booking_date ? format(parseISO(confirmCancel.booking_date), 'd MMMM yyyy', { locale: it }) : ''} · {confirmCancel.boat_name}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmCancel(null)} className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition">{t.back}</button>
              <button onClick={handleCancelConfirmed} disabled={actionLoading[confirmCancel.id]} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-medium rounded-xl transition">
                {actionLoading[confirmCancel.id] ? '...' : t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking edit modal */}
      {bookingEditId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{t.editBooking}</h3>
              <button onClick={() => setBookingEditId(null)} className="text-slate-400 hover:text-white transition">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide mb-1.5 block">{t.fieldDate}</label>
                <input
                  type="date"
                  value={bookingEditForm.booking_date}
                  onChange={e => setBookingEditForm(f => ({ ...f, booking_date: e.target.value }))}
                  className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide mb-1.5 block">{t.fieldGuests}</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setBookingEditForm(f => ({ ...f, guest_count: Math.max(1, f.guest_count - 1) }))}
                    className="w-9 h-9 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-bold flex items-center justify-center transition"
                  >−</button>
                  <input
                    type="number"
                    value={bookingEditForm.guest_count}
                    onChange={e => setBookingEditForm(f => ({ ...f, guest_count: parseInt(e.target.value) || 1 }))}
                    min="1"
                    className="w-20 bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-white text-sm text-center focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={() => setBookingEditForm(f => ({ ...f, guest_count: f.guest_count + 1 }))}
                    className="w-9 h-9 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-bold flex items-center justify-center transition"
                  >+</button>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-slate-400 uppercase tracking-wide mb-1.5 block">{t.fieldPriceLabel}</label>
                  <input
                    type="number"
                    value={bookingEditForm.total_price}
                    onChange={e => setBookingEditForm(f => ({ ...f, total_price: e.target.value }))}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-slate-400 uppercase tracking-wide mb-1.5 block">{t.fieldDepositLabel}</label>
                  <input
                    type="number"
                    value={bookingEditForm.deposit_required}
                    onChange={e => setBookingEditForm(f => ({ ...f, deposit_required: e.target.value }))}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wide mb-1.5 block">{t.internalNotes}</label>
                <textarea
                  value={bookingEditForm.internal_notes}
                  onChange={e => setBookingEditForm(f => ({ ...f, internal_notes: e.target.value }))}
                  rows={3}
                  className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500 resize-none"
                  placeholder={t.internalNotesPlaceholder}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setBookingEditId(null)}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition text-sm"
              >{t.cancel}</button>
              <button
                onClick={handleSaveBookingEdit}
                disabled={actionLoading[bookingEditId]}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition font-semibold text-sm disabled:opacity-50"
              >
                {actionLoading[bookingEditId] ? t.saving : t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close payment picker on outside click */}
      {paymentPickerId && (
        <div className="fixed inset-0 z-10" onClick={() => setPaymentPickerId(null)} />
      )}

      {detailBooking && (
        <BookingDetailModal
          booking={detailBooking}
          onClose={() => setDetailBooking(null)}
          onUpdated={(ub) => setBookings(bs => bs.map(x => x.id === ub.id ? { ...x, ...ub } : x))}
          onDeleted={(id) => { setBookings(bs => bs.filter(x => x.id !== id)); setDetailBooking(null); }}
        />
      )}

    </div>
  );
};
const RichiestePage = () => {
  const t = usePageT(adminTranslations);
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/bookings`, { headers });
      const data = (res.data || []).filter(b => b.booking_status === 'pending');
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/bookings/${id}`, { booking_status: 'confirmed' }, { headers });
      fetchRequests();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReject = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/bookings/${id}`, { booking_status: 'cancelled' }, { headers });
      fetchRequests();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">{t.requestsTitle}</h1>

      {requests.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-12 text-center">
          <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">{t.noRequestsPending}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req, idx) => (
            <div key={req.id || idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-white">{req.customer_name || t.newRequest}</h3>
                  <p className="text-sm text-slate-500">{req.customer_email || ''}</p>
                </div>
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  {t.statusPending}
                </span>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-slate-500">{t.fieldBoat}</p>
                  <p className="font-medium text-white">{req.boat_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t.fieldDate}</p>
                  <p className="font-medium text-white">
                    {req.booking_date ? format(parseISO(req.booking_date), 'd MMM yyyy', { locale: it }) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">{t.fieldGuests}</p>
                  <p className="font-medium text-white">{req.guest_count || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">{t.fieldTotal}</p>
                  <p className="font-medium text-white">€{(req.total_price || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleConfirm(req.id)}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  {t.confirm}
                </button>
                <button
                  onClick={() => handleReject(req.id)}
                  className="flex-1 py-3 bg-red-50 text-red-700 rounded-xl font-medium hover:bg-red-100 transition flex items-center justify-center gap-2"
                >
                  <XCircle className="w-5 h-5" />
                  {t.reject}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BOAT FORM MODAL ====================
const BoatFormModal = ({ boat, onClose, onSave, getAuthHeaders }) => {
  const t = usePageT(adminTranslations);
  const isEdit = !!boat?.id;
  const defaultSlotTimes = { morning_start: '09:00', morning_end: '13:00', afternoon_start: '14:00', afternoon_end: '18:00', sunset_start: '18:00', sunset_end: '21:00', full_day_start: '09:00', full_day_end: '18:00' };
  const defaultEnabledSlots = { morning: true, afternoon: true, full_day: true, sunset: true };

  const buildInitial = (b) => ({
    name: b?.name || '',
    model: b?.model || '',
    max_capacity: b?.max_capacity?.toString() || '',
    departure_port: b?.departure_port || '',
    weather_location: b?.weather_location || '',
    photo: b?.photo || null,
    is_active: b?.is_active !== false,
    notes: b?.notes || '',
    show_notes_public: b?.show_notes_public || false,
    tour_gruppo: b?.tour_gruppo || false,
    tour_esclusiva: b?.tour_esclusiva || false,
    locazione: b != null ? (b.locazione || false) : true,
    price_per_person: b?.price_per_person?.toString() || '',
    esclusiva_half_day: b?.esclusiva_half_day?.toString() || '',
    esclusiva_full_day: b?.esclusiva_full_day?.toString() || '',
    esclusiva_sunset: b?.esclusiva_sunset?.toString() || '',
    locazione_half_day: b?.locazione_half_day?.toString() || '',
    locazione_full_day: b?.locazione_full_day?.toString() || '',
    locazione_sunset: b?.locazione_sunset?.toString() || '',
    enabled_slots: b?.enabled_slots || { ...defaultEnabledSlots },
    slot_times: b?.slot_times || { ...defaultSlotTimes },
    booking_mode: b?.booking_mode || 'request',
    payment_type: b?.payment_type || 'full',
    deposit_percent: b?.deposit_percent || 30,
    skipper_available: b?.skipper_available || false,
    skipper_mode: b?.skipper_mode || 'on_request',
    skipper_daily_price: b?.skipper_daily_price?.toString() || '',
    multi_day_enabled: b?.multi_day_enabled || false,
    multi_day_min: b?.multi_day_min?.toString() || '1',
    multi_day_max: b?.multi_day_max?.toString() || '',
    noleggio_notes: b?.noleggio_notes || '',
    seasonal_pricing: b?.seasonal_pricing || [],
  });

  const [form, setForm] = useState(() => buildInitial(boat));
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errors, setErrors] = useState({});
  const [stripeActive, setStripeActive] = useState(null); // null=loading, true=ok, false=not connected
  const [activeSection, setActiveSection] = useState('base');
  const [showSeasonalForm, setShowSeasonalForm] = useState(false);
  const [editingRuleIdx, setEditingRuleIdx] = useState(null);
  const emptyRule = { name: '', start_date: '', end_date: '', price_per_person: '', esclusiva_half_day: '', esclusiva_full_day: '', esclusiva_sunset: '', locazione_half_day: '', locazione_full_day: '', locazione_sunset: '' };
  const [ruleForm, setRuleForm] = useState(emptyRule);

  useEffect(() => {
    axios.get(`${API}/stripe/account-status`, { headers: getAuthHeaders() })
      .then(r => setStripeActive(r.data.charges_enabled === true))
      .catch(() => setStripeActive(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setSlotTime = (key, val) => setForm(f => ({ ...f, slot_times: { ...f.slot_times, [key]: val } }));
  const setEnabledSlot = (key, val) => setForm(f => ({ ...f, enabled_slots: { ...f.enabled_slots, [key]: val } }));

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const headers = getAuthHeaders();
        const res = await axios.post(`${API}/upload/boat-image`, { image: ev.target.result, boat_id: boat?.id || 'new' }, { headers });
        set('photo', res.data.image_url);
      } catch {
        set('photo', ev.target.result);
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.boatNameRequired;
    if (!form.max_capacity || isNaN(parseInt(form.max_capacity)) || parseInt(form.max_capacity) < 1) e.max_capacity = t.boatCapacityRequired;
    if (!form.departure_port.trim()) e.departure_port = t.boatPortRequired;
    if (!form.tour_gruppo && !form.tour_esclusiva && !form.locazione) e.modes = t.modesSelectAtLeast;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { setActiveSection('base'); return; }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...form,
        max_capacity: parseInt(form.max_capacity) || 1,
        price_per_person: parseFloat(form.price_per_person) || 0,
        esclusiva_half_day: parseFloat(form.esclusiva_half_day) || 0,
        esclusiva_full_day: parseFloat(form.esclusiva_full_day) || 0,
        esclusiva_sunset: parseFloat(form.esclusiva_sunset) || 0,
        locazione_half_day: parseFloat(form.locazione_half_day) || 0,
        locazione_full_day: parseFloat(form.locazione_full_day) || 0,
        locazione_sunset: parseFloat(form.locazione_sunset) || 0,
        skipper_daily_price: parseFloat(form.skipper_daily_price) || 0,
        multi_day_min: parseInt(form.multi_day_min) || 1,
        multi_day_max: form.multi_day_max ? parseInt(form.multi_day_max) : null,
        deposit_percent: parseInt(form.deposit_percent) || 30,
      };
      if (isEdit) {
        await axios.put(`${API}/boats/${boat.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/boats`, payload, { headers });
      }
      onSave();
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || t.saveError });
    } finally {
      setSaving(false);
    }
  };

  const openSeasonalAdd = () => { setEditingRuleIdx(null); setRuleForm(emptyRule); setShowSeasonalForm(true); };
  const openSeasonalEdit = (idx) => {
    const r = form.seasonal_pricing[idx];
    setRuleForm({ name: r.name || '', start_date: r.start_date || '', end_date: r.end_date || '', price_per_person: r.price_per_person?.toString() || '', esclusiva_half_day: r.esclusiva_half_day?.toString() || '', esclusiva_full_day: r.esclusiva_full_day?.toString() || '', esclusiva_sunset: r.esclusiva_sunset?.toString() || '', locazione_half_day: r.locazione_half_day?.toString() || '', locazione_full_day: r.locazione_full_day?.toString() || '', locazione_sunset: r.locazione_sunset?.toString() || '' });
    setEditingRuleIdx(idx);
    setShowSeasonalForm(true);
  };
  const saveSeasonalRule = () => {
    const rule = { id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2), name: ruleForm.name, start_date: ruleForm.start_date, end_date: ruleForm.end_date, price_per_person: ruleForm.price_per_person ? parseFloat(ruleForm.price_per_person) : null, esclusiva_half_day: ruleForm.esclusiva_half_day ? parseFloat(ruleForm.esclusiva_half_day) : null, esclusiva_full_day: ruleForm.esclusiva_full_day ? parseFloat(ruleForm.esclusiva_full_day) : null, esclusiva_sunset: ruleForm.esclusiva_sunset ? parseFloat(ruleForm.esclusiva_sunset) : null, locazione_half_day: ruleForm.locazione_half_day ? parseFloat(ruleForm.locazione_half_day) : null, locazione_full_day: ruleForm.locazione_full_day ? parseFloat(ruleForm.locazione_full_day) : null, locazione_sunset: ruleForm.locazione_sunset ? parseFloat(ruleForm.locazione_sunset) : null };
    if (editingRuleIdx !== null) {
      set('seasonal_pricing', form.seasonal_pricing.map((r, i) => i === editingRuleIdx ? rule : r));
    } else {
      set('seasonal_pricing', [...form.seasonal_pricing, rule]);
    }
    setShowSeasonalForm(false);
    setEditingRuleIdx(null);
  };
  const removeSeasonalRule = (idx) => set('seasonal_pricing', form.seasonal_pricing.filter((_, i) => i !== idx));

  const inp = "w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm";
  const lbl = "block text-xs font-medium text-slate-400 mb-1.5";
  const Toggle = ({ on, onChange, color = 'bg-blue-600' }) => (
    <button type="button" onClick={() => onChange(!on)} className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${on ? color : 'bg-slate-600'} relative`}>
      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow ${on ? 'translate-x-7' : 'translate-x-1'}`} />
    </button>
  );

  const sections = [
    { id: 'base', label: t.sectionBase },
    { id: 'modalita', label: t.sectionModes },
    { id: 'fasce', label: t.sectionSlots },
    { id: 'pagamento', label: t.sectionPayment },
    { id: 'stagionale', label: t.sectionSeasonal },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl h-full bg-slate-900 border-l border-white/10 flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
          <h2 className="text-lg font-semibold text-white">{isEdit ? t.editBoat : t.newBoat}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition"><X className="w-5 h-5" /></button>
        </div>

        {/* Section tabs */}
        <div className="flex gap-0.5 px-4 pt-3 border-b border-white/10 overflow-x-auto flex-shrink-0 scrollbar-hide">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)} className={`px-3 py-2 text-xs font-medium rounded-t-lg whitespace-nowrap transition border-b-2 ${activeSection === s.id ? 'text-blue-400 border-blue-500 bg-blue-500/10' : 'text-slate-500 border-transparent hover:text-slate-300'}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* ─── INFO BASE ─── */}
          {activeSection === 'base' && (
            <div className="space-y-5">
              {/* Photo */}
              <div>
                <label className={lbl}>{t.boatPhoto}</label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-16 rounded-xl overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                    {form.photo ? <img src={form.photo} alt="preview" className="w-full h-full object-cover" /> : <Ship className="w-8 h-8 text-slate-600" />}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="cursor-pointer bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded-lg transition inline-flex items-center gap-2">
                      {uploadingPhoto ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      {uploadingPhoto ? t.photoUploading : t.choosePhoto}
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} disabled={uploadingPhoto} />
                    </label>
                    {form.photo && <button type="button" onClick={() => set('photo', null)} className="text-xs text-red-400 hover:text-red-300">{t.removePhoto}</button>}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={lbl}>{t.boatName}</label>
                  <input className={`${inp} ${errors.name ? 'border-red-500' : ''}`} value={form.name} onChange={e => set('name', e.target.value)} placeholder="es. Poseidon" />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className={lbl}>{t.boatModel}</label>
                  <input className={inp} value={form.model} onChange={e => set('model', e.target.value)} placeholder="es. Jeanneau 36" />
                </div>
                <div>
                  <label className={lbl}>{t.boatCapacity}</label>
                  <input className={`${inp} ${errors.max_capacity ? 'border-red-500' : ''}`} type="number" min="1" value={form.max_capacity} onChange={e => set('max_capacity', e.target.value)} placeholder="es. 10" />
                  {errors.max_capacity && <p className="text-red-400 text-xs mt-1">{errors.max_capacity}</p>}
                </div>
                <div className="col-span-2">
                  <label className={lbl}>{t.boatPort}</label>
                  <input className={`${inp} ${errors.departure_port ? 'border-red-500' : ''}`} value={form.departure_port} onChange={e => set('departure_port', e.target.value)} placeholder="es. Marina di Palermo" />
                  {errors.departure_port && <p className="text-red-400 text-xs mt-1">{errors.departure_port}</p>}
                </div>
                <div className="col-span-2">
                  <label className={lbl}>{t.boatWeatherZone} <span className="text-slate-500 normal-case font-normal">({t.boatWeatherZoneOptional})</span></label>
                  <input className={inp} value={form.weather_location} onChange={e => set('weather_location', e.target.value)} placeholder="es. Palermo" />
                  <p className="text-slate-500 text-xs mt-1">{t.boatWeatherZoneHint}</p>
                </div>
              </div>

              <div>
                <label className={lbl}>{t.boatNotes}</label>
                <textarea className={`${inp} resize-none`} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder={t.boatNotesPlaceholder} />
                <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                  <input type="checkbox" checked={form.show_notes_public} onChange={e => set('show_notes_public', e.target.checked)} className="w-4 h-4 rounded accent-blue-600" />
                  <span className="text-xs text-slate-400">{t.boatShowNotesPublic}</span>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/60 rounded-xl border border-white/5">
                <div>
                  <p className="text-sm font-medium text-white">{t.boatIsActive}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.boatIsActiveDesc}</p>
                </div>
                <Toggle on={form.is_active} onChange={v => set('is_active', v)} />
              </div>
            </div>
          )}

          {/* ─── MODALITÀ ─── */}
          {activeSection === 'modalita' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">{t.modesHint}</p>
              {errors.modes && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{errors.modes}</div>}

              {/* Tour in Gruppo */}
              <div className={`rounded-xl border p-4 transition ${form.tour_gruppo ? 'border-blue-500/40 bg-blue-500/5' : 'border-white/8 bg-slate-800/40'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div><p className="font-medium text-white text-sm">{t.tourGroupLabel}</p><p className="text-xs text-slate-500">{t.tourGroupDesc}</p></div>
                  <Toggle on={form.tour_gruppo} onChange={v => set('tour_gruppo', v)} />
                </div>
                {form.tour_gruppo && (
                  <div><label className={lbl}>{t.pricePerPerson}</label><input className={inp} type="number" min="0" step="0.01" value={form.price_per_person} onChange={e => set('price_per_person', e.target.value)} placeholder="0.00" /></div>
                )}
              </div>

              {/* Tour in Esclusiva */}
              <div className={`rounded-xl border p-4 transition ${form.tour_esclusiva ? 'border-purple-500/40 bg-purple-500/5' : 'border-white/8 bg-slate-800/40'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div><p className="font-medium text-white text-sm">{t.tourExclusiveLabel}</p><p className="text-xs text-slate-500">{t.tourExclusiveDesc}</p></div>
                  <Toggle on={form.tour_esclusiva} onChange={v => set('tour_esclusiva', v)} color="bg-purple-600" />
                </div>
                {form.tour_esclusiva && (
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className={lbl}>{t.priceHalfDay}</label><input className={inp} type="number" min="0" step="0.01" value={form.esclusiva_half_day} onChange={e => set('esclusiva_half_day', e.target.value)} placeholder="0.00" /></div>
                    <div><label className={lbl}>{t.priceFullDay}</label><input className={inp} type="number" min="0" step="0.01" value={form.esclusiva_full_day} onChange={e => set('esclusiva_full_day', e.target.value)} placeholder="0.00" /></div>
                    <div><label className={lbl}>{t.priceSunset}</label><input className={inp} type="number" min="0" step="0.01" value={form.esclusiva_sunset} onChange={e => set('esclusiva_sunset', e.target.value)} placeholder="0.00" /></div>
                  </div>
                )}
              </div>

              {/* Locazione */}
              <div className={`rounded-xl border p-4 transition ${form.locazione ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/8 bg-slate-800/40'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div><p className="font-medium text-white text-sm">{t.tourRentalLabel}</p><p className="text-xs text-slate-500">{t.tourRentalDesc}</p></div>
                  <Toggle on={form.locazione} onChange={v => set('locazione', v)} color="bg-amber-600" />
                </div>
                {form.locazione && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={lbl}>{t.priceHalfDay}</label><input className={inp} type="number" min="0" step="0.01" value={form.locazione_half_day} onChange={e => set('locazione_half_day', e.target.value)} placeholder="0.00" /></div>
                      <div><label className={lbl}>{t.priceFullDay}</label><input className={inp} type="number" min="0" step="0.01" value={form.locazione_full_day} onChange={e => set('locazione_full_day', e.target.value)} placeholder="0.00" /></div>
                      <div><label className={lbl}>{t.priceSunset}</label><input className={inp} type="number" min="0" step="0.01" value={form.locazione_sunset} onChange={e => set('locazione_sunset', e.target.value)} placeholder="0.00" /></div>
                    </div>
                    <div className="border-t border-white/8 pt-4">
                      <label className="flex items-center gap-3 cursor-pointer select-none mb-3">
                        <input type="checkbox" checked={form.skipper_available} onChange={e => set('skipper_available', e.target.checked)} className="w-4 h-4 rounded accent-blue-600" />
                        <span className="text-sm font-medium text-white">{t.skipperAvailable}</span>
                      </label>
                      {form.skipper_available && (
                        <div className="grid grid-cols-2 gap-3 ml-7">
                          <div>
                            <label className={lbl}>{t.skipperMode}</label>
                            <select className={inp} value={form.skipper_mode} onChange={e => set('skipper_mode', e.target.value)}>
                              <option value="on_request">{t.skipperOnRequest}</option>
                              <option value="mandatory">{t.skipperAlwaysIncluded}</option>
                            </select>
                          </div>
                          <div><label className={lbl}>{t.skipperPrice}</label><input className={inp} type="number" min="0" step="0.01" value={form.skipper_daily_price} onChange={e => set('skipper_daily_price', e.target.value)} placeholder="0.00" /></div>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-white/8 pt-4">
                      <label className="flex items-center gap-3 cursor-pointer select-none mb-3">
                        <input type="checkbox" checked={form.multi_day_enabled} onChange={e => set('multi_day_enabled', e.target.checked)} className="w-4 h-4 rounded accent-blue-600" />
                        <span className="text-sm font-medium text-white">{t.multiDayEnabled}</span>
                      </label>
                      {form.multi_day_enabled && (
                        <div className="grid grid-cols-2 gap-3 ml-7">
                          <div><label className={lbl}>{t.multiDayMin}</label><input className={inp} type="number" min="1" value={form.multi_day_min} onChange={e => set('multi_day_min', e.target.value)} placeholder="1" /></div>
                          <div><label className={lbl}>{t.multiDayMax}</label><input className={inp} type="number" min="1" value={form.multi_day_max} onChange={e => set('multi_day_max', e.target.value)} placeholder="∞" /></div>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-white/8 pt-4">
                      <label className={lbl}>{t.tourRentalLabel} ({t.boatWeatherZoneOptional})</label>
                      <textarea className={`${inp} resize-none`} rows={2} value={form.noleggio_notes} onChange={e => set('noleggio_notes', e.target.value)} placeholder={t.boatNotesPlaceholder} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── FASCE ORARIE ─── */}
          {activeSection === 'fasce' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">{t.sectionSlots}</p>
              {[
                { key: 'morning', label: t.morning, startKey: 'morning_start', endKey: 'morning_end' },
                { key: 'afternoon', label: t.afternoon, startKey: 'afternoon_start', endKey: 'afternoon_end' },
                { key: 'full_day', label: t.fullDay, startKey: 'full_day_start', endKey: 'full_day_end' },
                { key: 'sunset', label: t.sunset, startKey: 'sunset_start', endKey: 'sunset_end' },
              ].map(slot => {
                const on = form.enabled_slots?.[slot.key] !== false;
                return (
                  <div key={slot.key} className={`rounded-xl border p-4 transition ${on ? 'border-white/15 bg-slate-800/50' : 'border-white/5 bg-slate-800/20 opacity-55'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-white text-sm">{slot.label}</p>
                      <Toggle on={on} onChange={v => setEnabledSlot(slot.key, v)} />
                    </div>
                    {on && (
                      <div className="grid grid-cols-2 gap-4">
                        <div><label className={lbl}>Inizio</label><input type="time" className={inp} value={form.slot_times?.[slot.startKey] || ''} onChange={e => setSlotTime(slot.startKey, e.target.value)} /></div>
                        <div><label className={lbl}>Fine</label><input type="time" className={inp} value={form.slot_times?.[slot.endKey] || ''} onChange={e => setSlotTime(slot.endKey, e.target.value)} /></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── PAGAMENTO ─── */}
          {activeSection === 'pagamento' && (
            <div className="space-y-5">
              <div>
                <label className={lbl}>{t.bookingMode}</label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {/* Su richiesta — always available */}
                  <button type="button" onClick={() => set('booking_mode', 'request')} className={`p-4 rounded-xl border text-left transition ${form.booking_mode === 'request' ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 bg-slate-800 hover:border-white/20'}`}>
                    <p className="font-medium text-white text-sm">{t.bookingModeRequest}</p>
                    <p className="text-xs text-slate-500 mt-1">Ricevi la richiesta e la approvi manualmente</p>
                  </button>
                  {/* Con pagamento — only if Stripe charges_enabled */}
                  <button
                    type="button"
                    disabled={stripeActive === false}
                    onClick={() => stripeActive ? set('booking_mode', 'payment') : null}
                    className={`p-4 rounded-xl border text-left transition relative ${
                      form.booking_mode === 'payment' ? 'border-blue-500 bg-blue-500/10' :
                      stripeActive === false ? 'border-white/5 bg-slate-800/40 opacity-50 cursor-not-allowed' :
                      'border-white/10 bg-slate-800 hover:border-white/20'
                    }`}
                  >
                    <p className="font-medium text-white text-sm">{t.bookingModeInstant}</p>
                    <p className="text-xs text-slate-500 mt-1">Il cliente paga online per confermare subito</p>
                    {stripeActive === false && (
                      <span className="absolute top-2 right-2 text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-md">Stripe non attivo</span>
                    )}
                  </button>
                </div>
                {stripeActive === false && (
                  <p className="text-xs text-amber-400/80 mt-2">
                    Per abilitare i pagamenti online devi prima collegare il tuo account Stripe dalla sezione Account.
                  </p>
                )}
              </div>
              {form.booking_mode === 'payment' && stripeActive && (
                <div>
                  <label className={lbl}>{t.sectionPayment}</label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {[
                      { value: 'full', label: t.paymentFull, desc: '' },
                      { value: 'deposit', label: t.paymentDeposit, desc: '' },
                    ].map(opt => (
                      <button key={opt.value} type="button" onClick={() => set('payment_type', opt.value)} className={`p-4 rounded-xl border text-left transition ${form.payment_type === opt.value ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 bg-slate-800 hover:border-white/20'}`}>
                        <p className="font-medium text-white text-sm">{opt.label}</p>
                        <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {form.booking_mode === 'payment' && stripeActive && form.payment_type === 'deposit' && (
                <div>
                  <label className={lbl}>{t.depositPercent}</label>
                  <div className="flex gap-3 mt-2">
                    {[20, 30, 50].map(pct => (
                      <button key={pct} type="button" onClick={() => set('deposit_percent', pct)} className={`flex-1 py-3 rounded-xl border font-semibold text-sm transition ${form.deposit_percent === pct ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 bg-slate-800 text-slate-400 hover:border-white/20'}`}>
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── STAGIONALE ─── */}
          {activeSection === 'stagionale' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Prezzi diversi per periodi specifici (es. alta stagione). Hanno priorità sui prezzi base.</p>
                <button type="button" onClick={openSeasonalAdd} className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1 flex-shrink-0 ml-3">
                  <Plus className="w-4 h-4" /> {t.addSeasonalRule}
                </button>
              </div>

              {form.seasonal_pricing.length === 0 && !showSeasonalForm && (
                <div className="text-center py-10 text-slate-500 bg-slate-800/30 rounded-xl border border-white/5">
                  <p className="text-sm">{t.seasonalPricing}</p>
                  <p className="text-xs mt-1">Aggiungi regole per impostare prezzi diversi per periodi dell'anno</p>
                </div>
              )}

              {form.seasonal_pricing.map((rule, idx) => (
                <div key={rule.id || idx} className="bg-slate-800 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-white text-sm">{rule.name}</p>
                      <p className="text-xs text-slate-500">{rule.start_date} → {rule.end_date}</p>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => openSeasonalEdit(idx)} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10"><Edit className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => removeSeasonalRule(idx)} className="p-1.5 text-red-400 hover:text-red-300 rounded-lg hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                    {rule.price_per_person != null && <span>{t.typeGroupShort}: €{rule.price_per_person}/p</span>}
                    {rule.esclusiva_full_day != null && <span>{t.typeExclusiveShort} {t.fullDay}: €{rule.esclusiva_full_day}</span>}
                    {rule.esclusiva_half_day != null && <span>{t.typeExclusiveShort} {t.morning}: €{rule.esclusiva_half_day}</span>}
                    {rule.locazione_full_day != null && <span>{t.typeRental} {t.fullDay}: €{rule.locazione_full_day}</span>}
                    {rule.locazione_half_day != null && <span>{t.typeRental} {t.morning}: €{rule.locazione_half_day}</span>}
                  </div>
                </div>
              ))}

              {showSeasonalForm && (
                <div className="bg-slate-800 rounded-xl p-4 border border-blue-500/30 space-y-3">
                  <p className="font-medium text-white text-sm">{editingRuleIdx !== null ? t.edit : t.seasonalPricing}</p>
                  <div>
                    <label className={lbl}>{t.seasonalRuleName} *</label>
                    <input className={inp} value={ruleForm.name} onChange={e => setRuleForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Alta Stagione" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>{t.seasonalFrom} *</label><input type="date" className={inp} value={ruleForm.start_date} onChange={e => setRuleForm(f => ({ ...f, start_date: e.target.value }))} /></div>
                    <div><label className={lbl}>{t.seasonalTo} *</label><input type="date" className={inp} value={ruleForm.end_date} onChange={e => setRuleForm(f => ({ ...f, end_date: e.target.value }))} /></div>
                  </div>
                  {form.tour_gruppo && (
                    <div><label className={lbl}>{t.pricePerPerson} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.price_per_person} onChange={e => setRuleForm(f => ({ ...f, price_per_person: e.target.value }))} placeholder="—" /></div>
                  )}
                  {form.tour_esclusiva && (
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={lbl}>{t.priceHalfDay} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.esclusiva_half_day} onChange={e => setRuleForm(f => ({ ...f, esclusiva_half_day: e.target.value }))} placeholder="—" /></div>
                      <div><label className={lbl}>{t.priceFullDay} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.esclusiva_full_day} onChange={e => setRuleForm(f => ({ ...f, esclusiva_full_day: e.target.value }))} placeholder="—" /></div>
                      <div><label className={lbl}>{t.priceSunset} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.esclusiva_sunset} onChange={e => setRuleForm(f => ({ ...f, esclusiva_sunset: e.target.value }))} placeholder="—" /></div>
                    </div>
                  )}
                  {form.locazione && (
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={lbl}>{t.priceHalfDay} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.locazione_half_day} onChange={e => setRuleForm(f => ({ ...f, locazione_half_day: e.target.value }))} placeholder="—" /></div>
                      <div><label className={lbl}>{t.priceFullDay} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.locazione_full_day} onChange={e => setRuleForm(f => ({ ...f, locazione_full_day: e.target.value }))} placeholder="—" /></div>
                      <div><label className={lbl}>{t.priceSunset} (€)</label><input className={inp} type="number" min="0" step="0.01" value={ruleForm.locazione_sunset} onChange={e => setRuleForm(f => ({ ...f, locazione_sunset: e.target.value }))} placeholder="—" /></div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={saveSeasonalRule} disabled={!ruleForm.name || !ruleForm.start_date || !ruleForm.end_date} className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition">
                      {editingRuleIdx !== null ? t.update : t.addSeasonalRule}
                    </button>
                    <button type="button" onClick={() => { setShowSeasonalForm(false); setEditingRuleIdx(null); }} className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-xl transition">{t.cancel}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {errors.submit && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{errors.submit}</div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex gap-3 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-xl transition">{t.cancel}</button>
          <button type="button" onClick={handleSubmit} disabled={saving} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2">
            {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
            {saving ? t.saving : isEdit ? t.saveChanges : t.newBoat}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== BARCHE PAGE ====================
const BarchePage = () => {
  usePageMeta({ title: "Barche · Nautiqo" });
  const t = usePageT(adminTranslations);
  const { getAuthHeaders } = useAuth();
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBoat, setEditingBoat] = useState(null);
  const [deletingBoat, setDeletingBoat] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBoats(); }, []);

  const fetchBoats = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/boats?include_inactive=true`, { headers });
      setBoats(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (boat) => {
    setTogglingId(boat.id);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/boats/${boat.id}`, { is_active: !boat.is_active }, { headers });
      setBoats(bs => bs.map(b => b.id === boat.id ? { ...b, is_active: !b.is_active } : b));
    } catch (err) {
      console.error(err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deletingBoat) return;
    setDeleting(true);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/boats/${deletingBoat.id}`, { headers });
      setBoats(bs => bs.filter(b => b.id !== deletingBoat.id));
      setDeletingBoat(null);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const filteredBoats = showActiveOnly ? boats.filter(b => b.is_active !== false) : boats;

  if (loading) return <LoadingScreen />;

  return (
    <>
      <div className="p-4 lg:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{t.boatsTitle}</h1>
            <p className="text-slate-500">{filteredBoats.length} {t.navBoats}</p>
          </div>
          <button onClick={() => { setEditingBoat(null); setShowForm(true); }} className="bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 hover:bg-blue-700 transition text-sm">
            <Plus className="w-4 h-4" /> {t.addBoat}
          </button>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none mb-6">
          <input type="checkbox" checked={showActiveOnly} onChange={e => setShowActiveOnly(e.target.checked)} className="w-4 h-4 rounded accent-blue-600" />
          <span className="text-sm text-slate-400">{t.onlyActiveBoats}</span>
        </label>

        {boats.length === 0 ? (
          <div className="text-center py-20">
            <Ship className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-400 text-lg font-medium">{t.noBoats}</p>
            <p className="text-slate-600 text-sm mt-1">{t.addFirstBoat}</p>
            <button onClick={() => { setEditingBoat(null); setShowForm(true); }} className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition">{t.addBoat}</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBoats.map(boat => (
              <div key={boat.id} className={`bg-white/5 rounded-2xl border overflow-hidden transition ${boat.is_active !== false ? 'border-white/10' : 'border-white/5 opacity-55'}`}>
                <div className="h-40 bg-slate-800 flex items-center justify-center relative overflow-hidden">
                  {boat.photo ? <img src={boat.photo} alt={boat.name} className="w-full h-full object-cover" /> : <Ship className="w-14 h-14 text-slate-700" />}
                  <span className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${boat.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>
                    {boat.is_active !== false ? t.boatActive : t.boatInactive}
                  </span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <h3 className="font-semibold text-white">{boat.name}</h3>
                      <p className="text-sm text-slate-500">{boat.model || '—'}</p>
                    </div>
                    <span className="text-xs text-slate-500 flex items-center gap-1 flex-shrink-0"><Users className="w-3 h-3" />{boat.max_capacity}</span>
                  </div>
                  {boat.departure_port && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-3"><MapPin className="w-3 h-3" />{boat.departure_port}</div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {boat.tour_gruppo && <span className="px-2 py-0.5 bg-blue-500/15 text-blue-400 rounded-full text-xs">{t.typeGroupShort}</span>}
                    {boat.tour_esclusiva && <span className="px-2 py-0.5 bg-purple-500/15 text-purple-400 rounded-full text-xs">{t.typeExclusiveShort}</span>}
                    {boat.locazione && <span className="px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full text-xs">{t.typeRental}</span>}
                  </div>
                  <div className="flex gap-2 border-t border-white/5 pt-3">
                    <button onClick={() => handleToggleActive(boat)} disabled={togglingId === boat.id} className="flex-1 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition disabled:opacity-50">
                      {togglingId === boat.id ? '...' : boat.is_active !== false ? t.deactivate : t.activate}
                    </button>
                    <button onClick={() => { setEditingBoat(boat); setShowForm(true); }} className="flex-1 py-2 text-xs font-medium text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition flex items-center justify-center gap-1">
                      <Edit className="w-3 h-3" /> {t.edit}
                    </button>
                    <button onClick={() => setDeletingBoat(boat)} className="p-2 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {deletingBoat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">{t.deleteBoat}</h3>
            <p className="text-slate-400 text-sm mb-6">{t.deleteBoatConfirm} <strong className="text-white">{deletingBoat.name}</strong>? {t.deleteBoatIrreversible}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeletingBoat(null)} className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition">{t.cancel}</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-medium rounded-xl transition">
                {deleting ? t.deleting : t.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boat form */}
      {showForm && (
        <BoatFormModal
          boat={editingBoat}
          onClose={() => { setShowForm(false); setEditingBoat(null); }}
          onSave={() => { setShowForm(false); setEditingBoat(null); fetchBoats(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </>
  );
};

// ==================== ACCOUNT PAGE ====================
// ==================== FATTURATO PAGE ====================
const FatturatoPage = () => {
  usePageMeta({ title: "Fatturato · Nautiqo" });
  const t = usePageT(adminTranslations);
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reports, setReports] = useState(null);
  const [exporting, setExporting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get(`${API}/bookings`, { headers: getAuthHeaders() })
      .then(res => setBookings(Array.isArray(res.data) ? res.data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getPeriodRange = () => {
    const d = selectedDate;
    if (period === 'day') {
      const start = startOfDay(d);
      return { start, end: endOfDay(d), label: format(d, 'd MMMM yyyy', { locale: it }) };
    }
    if (period === 'week') {
      const start = startOfWeek(d, { weekStartsOn: 1 });
      const end = endOfWeek(d, { weekStartsOn: 1 });
      return { start, end, label: `${format(start, 'd MMM', { locale: it })} - ${format(end, 'd MMM yyyy', { locale: it })}` };
    }
    if (period === 'month') {
      const start = startOfMonth(d);
      const end = endOfMonth(d);
      return { start, end, label: format(d, 'MMMM yyyy', { locale: it }) };
    }
    // year
    const start = new Date(d.getFullYear(), 0, 1);
    const end = new Date(d.getFullYear(), 11, 31, 23, 59, 59);
    return { start, end, label: format(d, 'yyyy') };
  };

  const navigatePeriod = (dir) => {
    const d = selectedDate;
    if (period === 'day') setSelectedDate(addDays(d, dir));
    else if (period === 'week') setSelectedDate(addDays(d, dir * 7));
    else if (period === 'month') setSelectedDate(addMonths(d, dir));
    else setSelectedDate(new Date(d.getFullYear() + dir, d.getMonth(), d.getDate()));
  };

  const range = getPeriodRange();
  const dateFromStr = format(range.start, 'yyyy-MM-dd');
  const dateToStr = format(range.end, 'yyyy-MM-dd');
  const now = new Date();
  const isCurrentPeriod = now >= range.start && now <= range.end;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    setReports(null);
    axios.get(`${API}/reports`, { headers: getAuthHeaders(), params: { date_from: dateFromStr, date_to: dateToStr } })
      .then(res => { if (!cancelled) setReports(res.data); })
      .catch(() => { if (!cancelled) setReports(null); });
    return () => { cancelled = true; };
  }, [dateFromStr, dateToStr]);

  const filteredBookings = bookings.filter(b => {
    if (b.booking_status === 'cancelled') return false;
    const bd = parseISO(b.booking_date);
    return bd >= range.start && bd <= range.end;
  });

  const totals = {
    revenue: filteredBookings.reduce((s, b) => s + (b.total_price || 0), 0),
    deposits: filteredBookings.reduce((s, b) => s + (b.deposit_received || 0), 0),
    remaining: filteredBookings.reduce((s, b) => s + (b.remaining_balance || 0), 0),
    count: filteredBookings.length,
  };

  const KNOWN_TYPES = ['tour_gruppo', 'tour_esclusiva', 'locazione'];
  const typeConfig = {
    tour_gruppo:    { label: t.typeGroupTourLabel,     color: '#0D9488' },
    tour_esclusiva: { label: t.typeExclusiveTourLabel, color: '#7C3AED' },
    locazione:      { label: t.typeRentalLabel,        color: '#2563EB' },
    other:          { label: t.typeOtherLabel,         color: '#6B7280' },
  };

  // Local fallback with the same shape as /reports, used until the endpoint responds
  const aggregateLocally = () => {
    const emptyBucket = () => ({ count: 0, revenue: 0, deposits: 0, remaining: 0, guests: 0 });
    const addTo = (bucket, b) => {
      bucket.count += 1;
      bucket.revenue += b.total_price || 0;
      bucket.deposits += b.deposit_received || 0;
      bucket.remaining += b.remaining_balance || 0;
      bucket.guests += b.guest_count || 0;
    };
    const byType = {};
    const byBoat = {};
    filteredBookings.forEach(b => {
      const ty = KNOWN_TYPES.includes(b.booking_type) ? b.booking_type : 'other';
      if (!byType[ty]) byType[ty] = emptyBucket();
      addTo(byType[ty], b);
      const key = b.boat_id || b.boat_name || 'unknown';
      if (!byBoat[key]) byBoat[key] = { boat_id: key, boat_name: b.boat_name || t.boatUnknown, bookings_count: 0, revenue: 0, deposits: 0, remaining: 0, types: {} };
      const entry = byBoat[key];
      entry.bookings_count += 1;
      entry.revenue += b.total_price || 0;
      entry.deposits += b.deposit_received || 0;
      entry.remaining += b.remaining_balance || 0;
      if (!entry.types[ty]) entry.types[ty] = emptyBucket();
      addTo(entry.types[ty], b);
    });
    return { byType, byBoat: Object.values(byBoat) };
  };
  const localAgg = reports ? null : aggregateLocally();

  const typeBreakdown = reports ? (reports.revenue_by_booking_type || {}) : localAgg.byType;
  const activeTypeSummary = Object.entries(typeBreakdown)
    .filter(([, d]) => (d.revenue || 0) > 0)
    .sort((a, b) => b[1].revenue - a[1].revenue);
  const boatDetail = [...(reports ? (reports.revenue_by_boat_detail || []) : localAgg.byBoat)]
    .sort((a, b) => b.revenue - a.revenue);

  // Headline numbers come from the backend report when available — /bookings is capped
  // at 1000 documents, so client totals undercount large histories. Using the same
  // source for the % denominators keeps boat/type shares ≤ 100%.
  const headline = reports ? {
    revenue: reports.total_revenue || 0,
    deposits: reports.total_deposits_collected || 0,
    remaining: reports.total_remaining_balance || 0,
    count: Object.values(reports.revenue_by_booking_type || {}).reduce((s, d) => s + (d.count || 0), 0),
  } : totals;

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/export-csv`, {
        headers: getAuthHeaders(),
        params: { date_from: dateFromStr, date_to: dateToStr },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${dateFromStr}_${dateToStr}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(t.csvExportError);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto" data-testid="fatturato-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">{t.revenueTitle}</h1>
        <button onClick={exportCSV} disabled={exporting}
          className={`flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition ${exporting ? 'opacity-60 cursor-wait' : ''}`}
          data-testid="export-csv">
          <Download className="w-4 h-4" /> {t.exportCSV}
        </button>
      </div>

      {/* Period selector */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10 mb-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { value: 'day', label: t.periodDay },
            { value: 'week', label: t.periodWeek },
            { value: 'month', label: t.periodMonth },
            { value: 'year', label: t.periodYear },
          ].map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                period === p.value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`} data-testid={`period-${p.value}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <button onClick={() => navigatePeriod(-1)} className="p-2 hover:bg-slate-100 rounded-lg transition">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold text-white capitalize">{range.label}</span>
          <button onClick={() => navigatePeriod(1)} disabled={isCurrentPeriod}
            className={`p-2 rounded-lg transition ${isCurrentPeriod ? 'text-slate-500 opacity-50 cursor-not-allowed' : 'hover:bg-slate-100'}`}
            data-testid="period-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-slate-500 mb-1">{t.revenueTotal}</p>
          <p className="text-2xl font-bold text-white">€{headline.revenue.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-slate-500 mb-1">{t.revenueCollected}</p>
          <p className="text-2xl font-bold text-emerald-600">€{headline.deposits.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-slate-500 mb-1">{t.fieldRemaining}</p>
          <p className="text-2xl font-bold text-amber-600">€{headline.remaining.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-slate-500 mb-1">{t.revenueBookings}</p>
          <p className="text-2xl font-bold text-blue-600">{headline.count}</p>
        </div>
      </div>

      {/* By boat (unità navale) with per-type breakdown */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10 mb-6">
        <h2 className="font-semibold text-white mb-4">{t.revenueByBoat}</h2>
        {boatDetail.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">{t.noBookingsFound}</p>
        ) : (
          <div className="space-y-4">
            {boatDetail.map(boat => {
              const pct = headline.revenue > 0 ? Math.round(((boat.revenue || 0) / headline.revenue) * 100) : 0;
              const activeTypes = Object.entries(boat.types || {})
                .filter(([, d]) => (d.revenue || 0) > 0)
                .sort((a, b) => b[1].revenue - a[1].revenue);
              return (
                <div key={boat.boat_id} className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`boat-row-${boat.boat_name}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{boat.boat_name}</p>
                      <p className="text-xs text-slate-400">{boat.bookings_count} {t.revenueBookings}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white">€{(boat.revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-slate-400">{pct}% {t.revenueOfTotal}</p>
                    </div>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.max(pct, 3)}%` }} />
                  </div>
                  {activeTypes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                      {activeTypes.map(([key, data]) => {
                        const cfg = typeConfig[key] || typeConfig.other;
                        const tPct = (boat.revenue || 0) > 0 ? Math.round((data.revenue / boat.revenue) * 100) : 0;
                        return (
                          <div key={key} className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white">{cfg.label}</p>
                              <p className="text-xs text-slate-400">{data.count} {t.revenueBookings}{(data.guests || 0) > 0 ? ` · ${data.guests} ${t.revenueGuests}` : ''}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold" style={{ color: cfg.color }}>€{(data.revenue || 0).toLocaleString()}</p>
                              <p className="text-xs text-slate-400">{tPct}%</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {((boat.deposits || 0) > 0 || (boat.remaining || 0) > 0) && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex gap-4 text-xs">
                      {(boat.deposits || 0) > 0 && (
                        <span className="text-slate-400">{t.revenueDeposits}: <span className="text-emerald-400 font-medium">€{boat.deposits.toLocaleString()}</span></span>
                      )}
                      {(boat.remaining || 0) > 0 && (
                        <span className="text-slate-400">{t.revenueRemaining}: <span className="text-amber-400 font-medium">€{boat.remaining.toLocaleString()}</span></span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* By type — global summary */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10 mb-6">
        <h2 className="font-semibold text-white mb-4">{t.revenueByType}</h2>
        {activeTypeSummary.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">{t.noBookingsFound}</p>
        ) : (
          <div className="space-y-3">
            {activeTypeSummary.map(([type, data]) => {
              const cfg = typeConfig[type] || typeConfig.other;
              const pct = headline.revenue > 0 ? Math.round((data.revenue / headline.revenue) * 100) : 0;
              return (
                <div key={type} className="flex items-center gap-4" data-testid={`type-row-${type}`}>
                  <div className="w-36 flex-shrink-0 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
                    <span className="text-sm font-medium text-white">{cfg.label}</span>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                    </div>
                  </div>
                  <div className="w-32 text-right text-xs text-slate-400">{data.count} {t.revenueBookings}{(data.guests || 0) > 0 ? ` · ${data.guests} ${t.revenueGuests}` : ''}</div>
                  <div className="w-28 text-right">
                    <span className="font-bold text-white">€{(data.revenue || 0).toLocaleString()}</span>
                    <span className="block text-xs text-slate-400">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail table */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10">
          <h2 className="font-semibold text-white">{t.revenueBookings}</h2>
        </div>
        {filteredBookings.length === 0 ? (
          <p className="text-slate-400 text-sm py-8 text-center">{t.noBookingsFound}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 text-left">
                  <th className="px-4 py-3 font-medium text-slate-500">{t.navBookings}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.csvDate}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.csvBoat}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.csvCustomer}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.csvType}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.fieldTimeSlot}</th>
                  <th className="px-4 py-3 font-medium text-slate-500 text-right">{t.csvTotal}</th>
                  <th className="px-4 py-3 font-medium text-slate-500 text-right">{t.csvDeposit}</th>
                  <th className="px-4 py-3 font-medium text-slate-500 text-right">{t.csvBalance}</th>
                  <th className="px-4 py-3 font-medium text-slate-500">{t.csvPaymentStatus}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBookings.sort((a, b) => a.booking_date.localeCompare(b.booking_date)).map((b, i) => (
                  <tr key={b.id || i} className="hover:bg-slate-800/50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.booking_code || '-'}</td>
                    <td className="px-4 py-3 font-medium text-white whitespace-nowrap">
                      {b.booking_date ? format(parseISO(b.booking_date), 'd MMM', { locale: it }) : '-'}
                      {b.end_date && b.end_date !== b.booking_date && (
                        <span className="text-blue-600"> → {format(parseISO(b.end_date), 'd MMM', { locale: it })}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{b.boat_name || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{b.customer_name || '-'}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const cfg = typeConfig[b.booking_type] || typeConfig.other;
                        return (
                          <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${cfg.color}26`, color: cfg.color }}>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                      {b.time_slot === 'full_day' ? t.fullDay : b.time_slot === 'morning' ? t.morning : b.time_slot === 'afternoon' ? t.afternoon : b.time_slot === 'sunset' ? t.sunset : b.time_slot || '-'}
                      {b.duration_slot && ` · ${b.duration_slot}g`}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-white">€{(b.total_price || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">€{(b.deposit_received || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-amber-600">€{(b.remaining_balance || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        b.payment_status === 'fully_paid' ? 'bg-emerald-50 text-emerald-700' :
                        b.payment_status === 'deposit_paid' ? 'bg-blue-50 text-blue-700' :
                        'bg-red-50 text-red-700'
                      }`}>{b.payment_status === 'fully_paid' ? t.payPaid : b.payment_status === 'deposit_paid' ? t.paymentTypeDeposit : t.payUnpaidShort}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-800/50 font-semibold">
                  <td className="px-4 py-3" colSpan={6}>{t.revenueTotal}</td>
                  <td className="px-4 py-3 text-right">€{totals.revenue.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">€{totals.deposits.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-amber-600">€{totals.remaining.toLocaleString()}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// ==================== PROFILO PAGE ====================
const ToggleSwitch = ({ value, onChange, loading: swLoading }) => (
  <button
    onClick={() => !swLoading && onChange(!value)}
    className={`relative inline-flex w-11 h-6 rounded-full transition-colors ${value ? 'bg-emerald-500' : 'bg-slate-600'} ${swLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    aria-checked={value}
    role="switch"
  >
    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    {swLoading && <span className="absolute inset-0 flex items-center justify-center"><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /></span>}
  </button>
);

// CANCEL_POLICIES is defined inside ProfiloPage (using t for translated labels)

const WeatherSettingsTab = () => {
  const { getAuthHeaders } = useAuth();
  const t = usePageT(adminTranslations);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [waveThreshold, setWaveThreshold] = useState(1.5);
  const [windThreshold, setWindThreshold] = useState(25);
  const [autoBlock, setAutoBlock] = useState(false);
  const [boats, setBoats] = useState([]);
  // portForecasts: [{ port, forecast: [{date, wave_height, wind_speed_knots}], error? }]
  const [portForecasts, setPortForecasts] = useState([]);

  const ports = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    (boats || []).forEach(b => {
      const c = stripPortPrefix(b.departure_port);
      if (c && !seen.has(c.toLowerCase())) { seen.add(c.toLowerCase()); out.push(c); }
    });
    return out;
  }, [boats]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [settingsRes, boatsRes] = await Promise.allSettled([
        axios.get(`${API}/weather-settings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/boats`, { headers: getAuthHeaders() }),
      ]);
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const s = settingsRes.value.data;
        setEnabled(s.enabled ?? false);
        setWaveThreshold(s.wave_threshold ?? 1.5);
        setWindThreshold(s.wind_threshold ?? 25);
        setAutoBlock(s.auto_block ?? false);
      }
      if (boatsRes.status === 'fulfilled' && boatsRes.value.data) {
        setBoats(boatsRes.value.data);
      }
    } catch (e) { console.error('Weather settings load error:', e); }
    finally { setLoading(false); }
  };

  const loadForecast = async () => {
    setLoadingForecast(true);
    try {
      const res = await axios.get(`${API}/weather-forecast`, { headers: getAuthHeaders() });
      setPortForecasts(res.data?.ports || []);
    } catch (e) { alert(e.response?.data?.detail || t.weatherLoadError || 'Impossibile caricare le previsioni'); }
    finally { setLoadingForecast(false); }
  };

  const handleSave = async () => {
    if (!waveThreshold || waveThreshold <= 0) { alert(t.invalidWaveHeight || 'Altezza onda non valida'); return; }
    if (!windThreshold || windThreshold <= 0) { alert(t.invalidWindSpeed || 'Velocità vento non valida'); return; }
    if (enabled && ports.length === 0) { alert('Nessuna barca ha un porto di partenza impostato. Aggiungi il porto alle barche per attivare gli alert.'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/weather-settings`, {
        enabled, wave_threshold: parseFloat(waveThreshold), wind_threshold: parseFloat(windThreshold),
        auto_block: autoBlock,
      }, { headers: getAuthHeaders() });
      alert(t.tabWeather + ' ✓');
    } catch (e) { alert(e.response?.data?.detail || t.saveError || 'Errore durante il salvataggio'); }
    finally { setSaving(false); }
  };

  const sectionCls = "bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4";

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5">
      <div className={sectionCls}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center"><MapPin className="w-4 h-4 text-blue-400" /></div>
          <h3 className="font-semibold text-white">{t.tabWeather}</h3>
        </div>
        <p className="text-xs text-slate-400">{t.weatherPortsDesc}</p>
        {ports.length === 0 ? (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-300">{t.noPortsSet}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {ports.map(p => (
                <span key={p} className="inline-flex items-center gap-1 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1 text-xs font-medium text-blue-300">
                  <MapPin className="w-3 h-3" /> {p}
                </span>
              ))}
            </div>
            <button onClick={loadForecast} disabled={loadingForecast} className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 border border-blue-500/30 rounded-xl px-4 py-2 hover:bg-blue-500/10 transition disabled:opacity-50">
              {loadingForecast ? <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t.weatherRefresh}
            </button>
            {portForecasts.map((pf, pIdx) => (
              <div key={pIdx} className="space-y-1.5">
                <p className="text-sm font-semibold text-white">{pf.port}</p>
                {pf.error || !pf.forecast?.length ? (
                  <p className="text-xs text-slate-500 italic">{t.weatherUnavailable}</p>
                ) : (
                  <div className="bg-slate-800/50 rounded-xl border border-white/5 overflow-hidden">
                    {pf.forecast.slice(0, 7).map((day, i) => {
                      const wO = waveThreshold > 0 && day.wave_height !== null && day.wave_height > waveThreshold;
                      const wdO = windThreshold > 0 && day.wind_speed_knots !== null && day.wind_speed_knots > windThreshold;
                      const isAlert = wO || wdO;
                      return (
                        <div key={i} className={`flex items-center gap-4 px-4 py-2.5 ${i < pf.forecast.length - 1 ? 'border-b border-white/5' : ''} ${isAlert ? 'bg-red-500/5' : ''}`}>
                          <span className="text-xs text-slate-400 w-24 flex-shrink-0">{day.date}</span>
                          <div className="flex items-center gap-1 flex-1"><Waves className={`w-3.5 h-3.5 ${wO ? 'text-red-400' : 'text-slate-500'}`} /><span className={`text-xs ${wO ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>{day.wave_height !== null ? `${day.wave_height}m` : '—'}</span></div>
                          <div className="flex items-center gap-1 flex-1"><Wind className={`w-3.5 h-3.5 ${wdO ? 'text-red-400' : 'text-slate-500'}`} /><span className={`text-xs ${wdO ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>{day.wind_speed_knots !== null ? `${day.wind_speed_knots}kn` : '—'}</span></div>
                          {isAlert && <span className="text-xs font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded-lg">ALERT</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {boats.length > 0 && (
        <div className={sectionCls}>
          <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-cyan-500/20 flex items-center justify-center"><Ship className="w-4 h-4 text-cyan-400" /></div><h3 className="font-semibold text-white">{t.navBoats}</h3></div>
          <p className="text-xs text-slate-400">{t.fleetDesc}</p>
          <div className="space-y-1">
            {boats.map(boat => {
              const hasPort = !!stripPortPrefix(boat.departure_port);
              return (
                <div key={boat.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0"><Ship className="w-4 h-4 text-slate-400" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{boat.name}</p><p className="text-xs text-slate-500">{boat.departure_port || '—'}</p></div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-lg ${hasPort ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{hasPort ? t.portOK : t.portMissing}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={sectionCls}>
        <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center"><CloudLightning className="w-4 h-4 text-amber-400" /></div><h3 className="font-semibold text-white">{t.weatherAlertTitle}</h3></div>
        <div className="flex items-center justify-between gap-4">
          <div><p className="text-sm font-medium text-white">{t.weatherAlertEnabled}</p><p className="text-xs text-slate-400 mt-0.5">{t.weatherAlertEnabledDesc}</p></div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="sr-only peer" />
            <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
          </label>
        </div>
      </div>

      {enabled && (
        <div className={sectionCls}>
          <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-red-500/20 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-400" /></div><h3 className="font-semibold text-white">{t.alertThresholds}</h3></div>
          <p className="text-xs text-slate-400">{t.alertThresholdsDesc}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-400 mb-2"><Waves className="w-3.5 h-3.5 text-blue-400" /> {t.waveHeight} <span className="text-slate-500">(m)</span></label>
              <div className="flex items-center gap-2">
                <button onClick={() => setWaveThreshold(v => Math.max(0.1, Math.round((parseFloat(v) - 0.1) * 10) / 10))} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition flex items-center justify-center text-lg">−</button>
                <input type="number" step="0.1" min="0.1" value={waveThreshold} onChange={(e) => setWaveThreshold(parseFloat(e.target.value) || 0.1)} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-center text-sm focus:outline-none focus:border-blue-500/50 transition" />
                <button onClick={() => setWaveThreshold(v => Math.round((parseFloat(v) + 0.1) * 10) / 10)} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition flex items-center justify-center text-lg">+</button>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-400 mb-2"><Wind className="w-3.5 h-3.5 text-indigo-400" /> {t.windSpeed} <span className="text-slate-500">(kn)</span></label>
              <div className="flex items-center gap-2">
                <button onClick={() => setWindThreshold(v => Math.max(1, parseInt(v) - 1))} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition flex items-center justify-center text-lg">−</button>
                <input type="number" step="1" min="1" value={windThreshold} onChange={(e) => setWindThreshold(parseInt(e.target.value) || 1)} className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-center text-sm focus:outline-none focus:border-blue-500/50 transition" />
                <button onClick={() => setWindThreshold(v => parseInt(v) + 1)} className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition flex items-center justify-center text-lg">+</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {enabled && (
        <div className={sectionCls}>
          <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center"><Lock className="w-4 h-4 text-purple-400" /></div><h3 className="font-semibold text-white">{t.autoActionTitle}</h3></div>
          <p className="text-xs text-slate-400">{t.autoActionDesc}</p>
          <div className="space-y-2">
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${!autoBlock ? 'border-blue-500/40 bg-blue-500/10' : 'border-white/10 bg-white/5 hover:bg-white/8'}`}>
              <input type="radio" name="weather_action" checked={!autoBlock} onChange={() => setAutoBlock(false)} className="mt-0.5 accent-blue-500" />
              <div><p className={`text-sm font-medium ${!autoBlock ? 'text-white' : 'text-slate-300'}`}>{t.askBeforeBlock}</p><p className="text-xs text-slate-400 mt-0.5">{t.askBeforeBlockDesc}</p></div>
            </label>
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${autoBlock ? 'border-red-500/40 bg-red-500/10' : 'border-white/10 bg-white/5 hover:bg-white/8'}`}>
              <input type="radio" name="weather_action" checked={autoBlock} onChange={() => setAutoBlock(true)} className="mt-0.5 accent-red-500" />
              <div><p className={`text-sm font-medium ${autoBlock ? 'text-white' : 'text-slate-300'}`}>{t.autoBlockLabel}</p><p className="text-xs text-slate-400 mt-0.5">{t.autoBlockDesc}</p></div>
            </label>
          </div>
        </div>
      )}

      <button onClick={handleSave} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed">
        {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-5 h-5" />}
        {t.tabWeather}
      </button>
    </div>
  );
};

const ProfiloPage = () => {
  const t = usePageT(adminTranslations);
  const { getAuthHeaders, user: authUser } = useAuth();

  const CANCEL_POLICIES = {
    flexible: {
      label: t.cancelPolicyFlexible,
      color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      dot: 'bg-emerald-400',
      rules: t.cancelPolicyFlexibleRules,
    },
    moderate: {
      label: t.cancelPolicyModerate,
      color: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
      dot: 'bg-amber-400',
      rules: t.cancelPolicyModerateRules,
    },
    strict: {
      label: t.cancelPolicyStrict,
      color: 'bg-red-500/15 text-red-400 border-red-500/30',
      dot: 'bg-red-400',
      rules: t.cancelPolicyStrictRules,
    },
  };

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState('profilo');

  // New state
  const [bookingLink, setBookingLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [absorbFees, setAbsorbFees] = useState(false);
  const [savingAbsorb, setSavingAbsorb] = useState(false);
  const [cancelPolicy, setCancelPolicy] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ email_enabled: true, push_enabled: true });
  const [savingNotif, setSavingNotif] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    company_name: '', vat_number: '', sdi_code: '', pec_email: '',
    address: '', city: '', zip_code: '', website_url: '', logo_url: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, stripeRes, linkRes, cancelRes, notifRes] = await Promise.allSettled([
          axios.get(`${API}/profile`, { headers: getAuthHeaders() }),
          axios.get(`${API}/stripe/account-status`, { headers: getAuthHeaders() }),
          axios.get(`${API}/booking-link`, { headers: getAuthHeaders() }),
          axios.get(`${API}/company/cancellation-policy`, { headers: getAuthHeaders() }),
          axios.get(`${API}/notification-preferences`, { headers: getAuthHeaders() }),
        ]);
        if (profileRes.status === 'fulfilled') {
          const p = profileRes.value.data;
          setProfile(p);
          setForm({
            first_name: p.first_name || '',
            last_name: p.last_name || '',
            email: p.email || '',
            phone: p.phone || '',
            company_name: p.company_name || '',
            vat_number: p.vat_number || '',
            sdi_code: p.sdi_code || '',
            pec_email: p.pec_email || '',
            address: p.address || '',
            city: p.city || '',
            zip_code: p.zip_code || '',
            website_url: p.website_url || '',
            logo_url: p.logo_url || '',
          });
        }
        if (stripeRes.status === 'fulfilled') {
          const s = stripeRes.value.data;
          setStripeStatus(s);
          setAbsorbFees(s?.absorb_fees_enabled ?? false);
        }
        if (linkRes.status === 'fulfilled') setBookingLink(linkRes.value.data);
        if (cancelRes.status === 'fulfilled') setCancelPolicy(cancelRes.value.data?.cancellation_policy || 'flexible');
        if (notifRes.status === 'fulfilled') setNotifPrefs(notifRes.value.data);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    setSuccessMsg('');
    setTimeout(() => setErrorMsg(''), 4000);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result;
        const res = await axios.post(`${API}/upload/boat-image`, { image: base64 }, { headers: getAuthHeaders() });
        setForm(f => ({ ...f, logo_url: res.data.url }));
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch {
      showError('Errore nel caricamento del logo.');
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');
    try {
      const payload = { ...form };
      // don't send empty strings for optional fields
      if (!payload.vat_number) delete payload.vat_number;
      if (!payload.sdi_code) delete payload.sdi_code;
      if (!payload.pec_email) delete payload.pec_email;
      if (!payload.address) delete payload.address;
      if (!payload.city) delete payload.city;
      if (!payload.zip_code) delete payload.zip_code;
      if (!payload.website_url) delete payload.website_url;
      if (!payload.logo_url) delete payload.logo_url;
      await axios.put(`${API}/profile`, payload, { headers: getAuthHeaders() });
      showSuccess(t.profileUpdated);
    } catch (err) {
      showError(err.response?.data?.detail || t.saveError2);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    setPasswordError('');
    if (passwordForm.new_password.length < 8) { setPasswordError(t.passwordMinLength); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { setPasswordError(t.passwordMismatch); return; }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/profile`, { password: passwordForm.new_password }, { headers: getAuthHeaders() });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      showSuccess(t.passwordUpdated);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || t.passwordError);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleStripeConnect = async () => {
    setConnectingStripe(true);
    try {
      if (!stripeStatus?.connected) {
        await axios.post(`${API}/stripe/connect`, {}, { headers: getAuthHeaders() });
      }
      const res = await axios.post(`${API}/stripe/onboarding-link`, { return_url: window.location.href }, { headers: getAuthHeaders() });
      window.location.href = res.data.url;
    } catch (err) {
      showError(err.response?.data?.detail || 'Errore connessione Stripe.');
      setConnectingStripe(false);
    }
  };

  const fetchStripeStatus = async () => {
    setStripeLoading(true);
    try {
      const res = await axios.get(`${API}/stripe/account-status`, { headers: getAuthHeaders() });
      setStripeStatus(res.data);
      setAbsorbFees(res.data?.absorb_fees_enabled ?? false);
    } finally {
      setStripeLoading(false);
    }
  };

  const handleToggleAbsorbFees = async (val) => {
    setSavingAbsorb(true);
    try {
      await axios.put(`${API}/stripe/absorb-fees`, { enabled: val, percent: 5.0 }, { headers: getAuthHeaders() });
      setAbsorbFees(val);
      showSuccess(val ? t.absorbFeesActive : t.absorbFeesInactive);
    } catch (err) {
      showError(err.response?.data?.detail || 'Errore nel salvataggio.');
    } finally {
      setSavingAbsorb(false);
    }
  };

  const handleSaveCancelPolicy = async (policy) => {
    setSavingPolicy(true);
    try {
      await axios.put(`${API}/company/cancellation-policy`, { cancellation_policy: policy }, { headers: getAuthHeaders() });
      setCancelPolicy(policy);
      showSuccess(t.cancelPolicySaved);
    } catch (err) {
      showError(err.response?.data?.detail || 'Errore nel salvataggio.');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleToggleNotif = async (key, val) => {
    setSavingNotif(true);
    const updated = { ...notifPrefs, [key]: val };
    try {
      const res = await axios.put(`${API}/notification-preferences`, updated, { headers: getAuthHeaders() });
      setNotifPrefs(res.data);
    } catch {
      // revert
    } finally {
      setSavingNotif(false);
    }
  };

  const handleCopyLink = () => {
    if (!bookingLink?.url) return;
    navigator.clipboard.writeText(bookingLink.url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const tabs = [
    { id: 'profilo', label: t.tabProfile },
    { id: 'prenotazioni', label: t.tabBookings },
    { id: 'pagamenti', label: t.tabPayments },
    { id: 'notifiche', label: t.tabNotifications },
    ...(authUser?.role !== 'staff' ? [{ id: 'meteo', label: t.tabWeather }] : []),
  ];

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition text-sm";
  const labelClass = "block text-xs font-medium text-slate-400 mb-1.5";
  const sectionClass = "bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4";
  const rowClass = "flex items-center justify-between py-3 border-b border-white/5 last:border-0";

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{t.settingsTitle}</h1>
        <p className="text-slate-400 text-sm mt-1">{t.settingsSubtitle}</p>
      </div>

      {successMsg && (
        <div className="mb-5 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3 text-emerald-400 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />{successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mb-5 flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />{errorMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition whitespace-nowrap px-3 ${activeTab === t.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Profilo ── */}
      {activeTab === 'profilo' && (
        <div className="space-y-5">
          {/* Profile card */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
              {form.logo_url ? <img src={form.logo_url} alt="Logo" className="w-full h-full object-cover" /> : <Ship className="w-7 h-7 text-slate-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate">{form.company_name || profile?.company_name}</p>
              <p className="text-sm text-slate-400 truncate">{form.email}</p>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${
                ['active'].includes(authUser?.subscription_status) ? 'bg-emerald-500/15 text-emerald-400' :
                ['trial','trialing'].includes(authUser?.subscription_status) ? 'bg-amber-500/15 text-amber-400' :
                'bg-red-500/15 text-red-400'
              }`}>
                {authUser?.subscription_status === 'active' ? t.subscriptionActive :
                 ['trial','trialing'].includes(authUser?.subscription_status) ? t.subscriptionTrial : t.subscriptionExpired}
              </span>
            </div>
            <div>
              <label className="cursor-pointer">
                <span className={`inline-flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/15 text-white text-xs font-medium rounded-xl transition ${uploadingLogo ? 'opacity-60 pointer-events-none' : ''}`}>
                  {uploadingLogo ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />{t.logoUploading}</> : t.changeLogo}
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} />
              </label>
            </div>
          </div>

          {/* Dati personali */}
          <div className={sectionClass}>
            <h3 className="font-semibold text-white text-sm">{t.personalData}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>{t.fieldFirstName}</label><input className={inputClass} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="Marco" /></div>
              <div><label className={labelClass}>{t.fieldLastName}</label><input className={inputClass} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Rossi" /></div>
            </div>
            <div><label className={labelClass}>{t.fieldEmail}</label><input className={inputClass} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="tu@email.com" /></div>
            <div><label className={labelClass}>{t.fieldPhone}</label><input className={inputClass} type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+39 333 1234567" /></div>
          </div>

          {/* Dati azienda */}
          <div className={sectionClass}>
            <h3 className="font-semibold text-white text-sm">{t.companyData}</h3>
            <div><label className={labelClass}>{t.companyName}</label><input className={inputClass} value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Es. Seawave Charter" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>{t.vatNumber}</label><input className={inputClass} value={form.vat_number} onChange={e => setForm(f => ({ ...f, vat_number: e.target.value }))} placeholder="IT12345678901" /></div>
              <div><label className={labelClass}>{t.sdiCode}</label><input className={inputClass} value={form.sdi_code} onChange={e => setForm(f => ({ ...f, sdi_code: e.target.value }))} placeholder="AAABBB1" /></div>
            </div>
            <div><label className={labelClass}>{t.pec}</label><input className={inputClass} type="email" value={form.pec_email} onChange={e => setForm(f => ({ ...f, pec_email: e.target.value }))} placeholder="azienda@pec.it" /></div>
            <div><label className={labelClass}>{t.address}</label><input className={inputClass} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Via Roma 1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>{t.city}</label><input className={inputClass} value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Palermo" /></div>
              <div><label className={labelClass}>{t.zip}</label><input className={inputClass} value={form.zip_code} onChange={e => setForm(f => ({ ...f, zip_code: e.target.value }))} placeholder="90100" /></div>
            </div>
            <div><label className={labelClass}>{t.website}</label><input className={inputClass} value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} placeholder="https://www.azienda.it" /></div>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2">
            {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t.saving}</> : t.saveChanges}
          </button>

          {/* Password change */}
          <div className={sectionClass}>
            <h3 className="font-semibold text-white text-sm">{t.changePassword}</h3>
            <div><label className={labelClass}>{t.newPassword}</label><input className={inputClass} type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} placeholder={t.newPasswordPlaceholder} /></div>
            <div><label className={labelClass}>{t.confirmPassword}</label><input className={inputClass} type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} placeholder={t.confirmPasswordPlaceholder} /></div>
            {passwordError && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5"><AlertCircle className="w-4 h-4 flex-shrink-0" />{passwordError}</div>}
          </div>
          <button onClick={handlePasswordSave} disabled={savingPassword}
            className="w-full py-3 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl transition flex items-center justify-center gap-2 text-sm">
            {savingPassword ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t.updatingPassword}</> : t.updatePassword}
          </button>
        </div>
      )}

      {/* ── Tab: Prenotazioni online ── */}
      {activeTab === 'prenotazioni' && (
        <div className="space-y-5">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <h3 className="font-semibold text-white mb-1">{t.publicBookingLink}</h3>
            <p className="text-sm text-slate-400 mb-4">{t.publicBookingLinkDesc}</p>
            {bookingLink?.blocked ? (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-amber-400 text-sm">
                <p className="font-medium">{t.featureNotAvailable}</p>
                <p className="text-xs text-amber-300/70 mt-1">{t.upgradeToEnable}</p>
              </div>
            ) : bookingLink?.url ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3">
                  <span className="flex-1 text-sm text-slate-300 truncate font-mono">{bookingLink.url}</span>
                  <button onClick={handleCopyLink} className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition ${linkCopied ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 hover:bg-white/15 text-white'}`}>
                    {linkCopied ? <><CheckCircle className="w-3.5 h-3.5" />{t.copiedBang}</> : <><Copy className="w-3.5 h-3.5" />{t.copy}</>}
                  </button>
                  <a href={bookingLink.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition">
                    <ExternalLink className="w-3.5 h-3.5" />{t.open}
                  </a>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: t.siteWebLabel, desc: t.siteWebDesc },
                    { label: t.instagramLabel, desc: t.instagramDesc },
                    { label: t.whatsappLabel, desc: t.whatsappDesc },
                  ].map(c => (
                    <div key={c.label} className="bg-white/5 rounded-xl p-3 text-center">
                      <p className="text-xs font-semibold text-white">{c.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-sm">
            <p className="font-medium text-blue-300 mb-2">{t.howToIntegrate}</p>
            <ol className="text-slate-400 space-y-1.5 text-xs list-decimal list-inside">
              <li>{t.integrationStep1}</li>
              <li>{t.integrationStep2}</li>
              <li>{t.integrationStep3}</li>
              <li>{t.integrationStep4}</li>
            </ol>
          </div>
        </div>
      )}

      {/* ── Tab: Pagamenti ── */}
      {activeTab === 'pagamenti' && (
        <div className="space-y-5">
          {/* Stripe Connect */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Stripe Connect</h3>
              <button onClick={fetchStripeStatus} disabled={stripeLoading} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition">
                <RefreshCw className={`w-3.5 h-3.5 ${stripeLoading ? 'animate-spin' : ''}`} />{t.update}
              </button>
            </div>

            {/* Status */}
            <div className={`flex items-center gap-3 p-3 rounded-xl border mb-4 ${stripeStatus?.charges_enabled ? 'bg-emerald-500/10 border-emerald-500/30' : stripeStatus?.connected ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-700/30 border-white/10'}`}>
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${stripeStatus?.charges_enabled ? 'bg-emerald-400' : stripeStatus?.connected ? 'bg-amber-400' : 'bg-slate-500'}`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${stripeStatus?.charges_enabled ? 'text-emerald-400' : stripeStatus?.connected ? 'text-amber-400' : 'text-slate-400'}`}>
                  {stripeStatus?.charges_enabled ? t.stripeActive : stripeStatus?.connected ? t.stripeVerifying : t.stripeNotConfigured}
                </p>
                {stripeStatus?.stripe_account_id && <p className="text-xs text-slate-500 font-mono mt-0.5">{stripeStatus.stripe_account_id}</p>}
              </div>
              {!stripeStatus?.charges_enabled && (
                <button onClick={handleStripeConnect} disabled={connectingStripe}
                  className="flex-shrink-0 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-semibold rounded-lg transition flex items-center gap-1.5">
                  {connectingStripe ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />{t.loading}</> : stripeStatus?.connected ? t.stripeComplete : t.stripeConnect}
                </button>
              )}
            </div>

            {/* Detail grid if connected */}
            {stripeStatus?.connected && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-slate-500">{t.stripeCharges}</p>
                  <p className={`text-sm font-semibold mt-0.5 ${stripeStatus.charges_enabled ? 'text-emerald-400' : 'text-amber-400'}`}>{stripeStatus.charges_enabled ? '✓' : t.statusPending}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-slate-500">{t.stripePayouts}</p>
                  <p className={`text-sm font-semibold mt-0.5 ${stripeStatus.payouts_enabled ? 'text-emerald-400' : 'text-amber-400'}`}>{stripeStatus.payouts_enabled ? '✓' : t.statusPending}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-slate-500">{t.stripeCommission}</p>
                  <p className="text-sm font-semibold mt-0.5 text-white">{stripeStatus.platform_fee_percent ?? 3}% Nautiqo</p>
                </div>
              </div>
            )}
          </div>

          {/* Absorb fees toggle — only if Stripe connected */}
          {stripeStatus?.connected && (
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className={rowClass + ' px-5'}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${absorbFees ? 'bg-emerald-500/15' : 'bg-slate-600/30'}`}>
                    <TrendingUp className={`w-4 h-4 ${absorbFees ? 'text-emerald-400' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.absorbFees}</p>
                    <p className="text-xs text-slate-400">{t.absorbFeesDesc}</p>
                  </div>
                </div>
                <ToggleSwitch value={absorbFees} onChange={handleToggleAbsorbFees} loading={savingAbsorb} />
              </div>
              <div className="px-5 pb-4 pt-0">
                <div className="bg-slate-900/40 rounded-xl p-3 text-xs text-slate-400">
                  {absorbFees
                    ? t.absorbFeesActive
                    : t.absorbFeesInactive
                  }
                </div>
              </div>
            </div>
          )}

          {/* Politica di cancellazione */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <h3 className="font-semibold text-white mb-1">{t.cancelPolicy}</h3>
            <p className="text-xs text-slate-400 mb-4">{t.cancelPolicyDesc}</p>
            <div className="space-y-2">
              {Object.entries(CANCEL_POLICIES).map(([key, pol]) => (
                <button
                  key={key}
                  onClick={() => !savingPolicy && handleSaveCancelPolicy(key)}
                  disabled={savingPolicy}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition ${cancelPolicy === key ? `${pol.color} border` : 'bg-white/5 border-white/10 hover:bg-white/8'} ${savingPolicy ? 'opacity-60' : ''}`}
                >
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ${pol.dot}`} />
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${cancelPolicy === key ? '' : 'text-white'}`}>{pol.label}</p>
                    <p className={`text-xs mt-0.5 ${cancelPolicy === key ? 'opacity-80' : 'text-slate-500'}`}>{pol.rules}</p>
                  </div>
                  {cancelPolicy === key && <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {t.universalClauses}
            </p>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-xs text-slate-400">
            <p className="font-medium text-blue-300 mb-1">{t.stripeHowTitle}</p>
            <p>{t.stripeHowDesc}</p>
          </div>
        </div>
      )}

      {/* ── Tab: Notifiche ── */}
      {activeTab === 'notifiche' && (
        <div className="space-y-5">
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
            <div className="px-5 pt-4 pb-2">
              <h3 className="font-semibold text-white text-sm">{t.tabNotifications}</h3>
              <p className="text-xs text-slate-400 mt-1">{t.notifEmailDesc}</p>
            </div>
            <div className={rowClass + ' px-5'}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-500/15 rounded-xl flex items-center justify-center">
                  <Mail className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t.notifEmail}</p>
                  <p className="text-xs text-slate-400">{t.notifEmailDesc}</p>
                </div>
              </div>
              <ToggleSwitch value={notifPrefs.email_enabled} onChange={val => handleToggleNotif('email_enabled', val)} loading={savingNotif} />
            </div>
            <div className={rowClass + ' px-5'}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-indigo-500/15 rounded-xl flex items-center justify-center">
                  <Bell className="w-4 h-4 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t.notifPush}</p>
                  <p className="text-xs text-slate-400">{t.notifPushDesc}</p>
                </div>
              </div>
              <ToggleSwitch value={notifPrefs.push_enabled} onChange={val => handleToggleNotif('push_enabled', val)} loading={savingNotif} />
            </div>
          </div>

          <div className="bg-slate-700/30 border border-white/10 rounded-xl p-4 text-xs text-slate-400">
            Le notifiche push arrivano solo sul dispositivo mobile con l'app Nautiqo installata. Le email sono sempre disponibili da web e app.
          </div>
        </div>
      )}
      {activeTab === 'meteo' && authUser?.role !== 'staff' && <WeatherSettingsTab />}
    </div>
  );
};

// ==================== FLOTTA PAGE ====================
// ==================== NUOVA PRENOTAZIONE ====================
const NuovaPrenotazionePage = () => {
  const t = usePageT(adminTranslations);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [boats, setBoats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [customDeposit, setCustomDeposit] = useState('');
  const [customTotalPrice, setCustomTotalPrice] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [seasonalPrice, setSeasonalPrice] = useState(null); // null = not yet fetched
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [formData, setFormData] = useState({
    booking_date: '',
    end_date: '',          // multi-day locazione only
    time_slot: '',
    boat_id: '',
    boat_name: '',
    customer_id: '',
    customer_display: '',
    new_first_name: '',
    new_last_name: '',
    new_phone: '',
    new_email: '',
    guest_count: 1,
    booking_type: 'tour_gruppo',
    payment_method: 'cash',   // API values: cash | card | bank_transfer | payment_link | other
    customer_notes: '',
    internal_notes: '',
    booking_source: 'manual', // manual | website | phone | whatsapp | agency
    skipper_included: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${API}/boats`, { headers }).catch(() => axios.get(`${API}/public/boats`)),
      axios.get(`${API}/customers`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/bookings`, { headers }).catch(() => ({ data: [] }))
    ]).then(([boatsRes, customersRes, bookingsRes]) => {
      setBoats(Array.isArray(boatsRes.data) ? boatsRes.data : []);
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setAllBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
    });
  }, []);

  const selectedBoat = boats.find(b => b.id === formData.boat_id);

  // Duration in days for multi-day locazione
  const durationDays = (() => {
    if (formData.booking_type !== 'locazione' || !selectedBoat?.multi_day_enabled) return 1;
    if (!formData.end_date || !formData.booking_date) return 1;
    const start = new Date(formData.booking_date);
    const end = new Date(formData.end_date);
    const diff = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff + 1 : 1;
  })();

  // Fetch seasonal price from API when date + boat + slot + type are all set
  useEffect(() => {
    const { boat_id, booking_date, time_slot, booking_type } = formData;
    if (!boat_id || !booking_date || !time_slot || !booking_type) { setSeasonalPrice(null); return; }
    setLoadingPrice(true);
    const headers = getAuthHeaders();
    axios.get(`${API}/boats/${boat_id}/price`, { params: { date: booking_date, time_slot, booking_type }, headers })
      .then(r => setSeasonalPrice(r.data.price ?? null))
      .catch(() => setSeasonalPrice(null))
      .finally(() => setLoadingPrice(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.boat_id, formData.booking_date, formData.time_slot, formData.booking_type]);

  // Reset seasonal price and end_date when boat or type changes
  useEffect(() => {
    setSeasonalPrice(null);
    setCustomTotalPrice(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.boat_id, formData.time_slot, formData.booking_type]);

  const getAvailableSlots = (boat) => {
    if (!boat?.enabled_slots) return [];
    const slotLabels = { morning: t.morning, afternoon: t.afternoon, full_day: t.fullDay, sunset: t.sunset };
    return Object.entries(boat.enabled_slots)
      .filter(([, enabled]) => enabled)
      .map(([key]) => ({ value: key, label: slotLabels[key] || key }));
  };

  // Base unit price: use seasonal price from API, fall back to boat static prices
  const unitPrice = (() => {
    if (seasonalPrice !== null) return seasonalPrice;
    if (!selectedBoat) return 0;
    const { booking_type, time_slot } = formData;
    if (booking_type === 'tour_gruppo') return selectedBoat.price_per_person || 0;
    if (booking_type === 'tour_esclusiva') {
      if (time_slot === 'full_day') return selectedBoat.esclusiva_full_day || selectedBoat.full_day_price || 0;
      if (time_slot === 'morning' || time_slot === 'afternoon') return selectedBoat.esclusiva_half_day || selectedBoat.half_day_price || 0;
      if (time_slot === 'sunset') return selectedBoat.esclusiva_sunset || selectedBoat.sunset_price || 0;
    }
    if (booking_type === 'locazione') {
      if (time_slot === 'full_day') return selectedBoat.locazione_full_day || selectedBoat.full_day_price || 0;
      if (time_slot === 'morning' || time_slot === 'afternoon') return selectedBoat.locazione_half_day || selectedBoat.half_day_price || 0;
      if (time_slot === 'sunset') return selectedBoat.locazione_sunset || selectedBoat.sunset_price || 0;
    }
    return selectedBoat.full_day_price || 0;
  })();

  const skipperCost = (formData.skipper_included && selectedBoat?.skipper_available)
    ? (selectedBoat.skipper_daily_price || 0) * durationDays : 0;

  const calculatePrice = () => {
    if (!selectedBoat) return 0;
    const { booking_type, guest_count } = formData;
    let base = 0;
    if (booking_type === 'tour_gruppo') base = unitPrice * guest_count;
    else base = unitPrice * durationDays;
    return base + skipperCost;
  };

  const suggestedPrice = calculatePrice();
  const finalTotalPrice = customTotalPrice !== null ? customTotalPrice : suggestedPrice;
  const depositAmount = customDeposit !== '' ? (parseFloat(customDeposit) || 0) : 0;

  // Filter boats by availability on selected date/time
  const getAvailableBoats = () => {
    if (!formData.booking_date) return boats.filter(b => b.is_active !== false);
    return boats.filter(b => {
      if (b.is_active === false) return false;
      const conflicting = allBookings.filter(bk =>
        bk.boat_id === b.id &&
        bk.booking_date === formData.booking_date &&
        bk.booking_status !== 'cancelled'
      );
      if (conflicting.length === 0) return true;
      // If any booking is full_day, boat is unavailable
      if (conflicting.some(bk => bk.time_slot === 'full_day')) return false;
      // If user wants full_day and there's any booking, unavailable
      if (formData.time_slot === 'full_day' && conflicting.length > 0) return false;
      // Check specific slot conflict
      if (formData.time_slot && conflicting.some(bk => bk.time_slot === formData.time_slot)) return false;
      return true;
    });
  };

  const getBookingTypes = () => {
    if (!selectedBoat) return [];
    const types = [];
    if (selectedBoat.tour_gruppo) types.push({ value: 'tour_gruppo', label: t.typeGroup });
    if (selectedBoat.tour_esclusiva) types.push({ value: 'tour_esclusiva', label: t.typeExclusive });
    if (selectedBoat.locazione) types.push({ value: 'locazione', label: t.typeRental });
    if (types.length === 0) types.push({ value: 'tour_gruppo', label: t.typeGroup });
    return types;
  };

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return (c.first_name || '').toLowerCase().includes(q) ||
           (c.last_name || '').toLowerCase().includes(q) ||
           (c.phone || '').includes(q) ||
           (c.email || '').toLowerCase().includes(q);
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const headers = getAuthHeaders();
      let customerId = formData.customer_id;

      if (isNewCustomer) {
        if (!formData.new_first_name || !formData.new_last_name) {
          setError(t.nameRequired);
          setSubmitting(false);
          return;
        }
        const customerRes = await axios.post(`${API}/customers`, {
          first_name: formData.new_first_name,
          last_name: formData.new_last_name,
          phone: formData.new_phone || '',
          email: formData.new_email || '',
        }, { headers });
        customerId = customerRes.data.id;
      }

      if (!customerId) {
        setError(t.customerRequired);
        setSubmitting(false);
        return;
      }

      const isMultiDay = formData.booking_type === 'locazione' && selectedBoat?.multi_day_enabled && formData.end_date && durationDays > 1;
      const payload = {
        booking_date: formData.booking_date,
        ...(isMultiDay && { end_date: formData.end_date, duration_days: durationDays }),
        time_slot: formData.time_slot,
        boat_id: formData.boat_id,
        customer_id: customerId,
        guest_count: formData.guest_count,
        total_price: finalTotalPrice,
        booking_type: formData.booking_type,
        deposit_required: depositAmount,
        payment_method: formData.payment_method,
        customer_notes: formData.customer_notes,
        internal_notes: formData.internal_notes,
        booking_source: formData.booking_source,
        ...(formData.booking_type === 'locazione' && selectedBoat?.skipper_available && { skipper_included: formData.skipper_included }),
      };

      await axios.post(`${API}/bookings`, payload, { headers });
      navigate('/admin/prenotazioni');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(formatApiError(detail) || t.bookingError);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = formData.booking_date;
  const canProceedStep2 = formData.boat_id && formData.time_slot;
  const canProceedStep3 = formData.customer_id || (isNewCustomer && formData.new_first_name && formData.new_last_name);
  const canProceedStep4 = formData.guest_count > 0 && formData.booking_type;

  return (
    <div className="p-4 lg:p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-button">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-white">{t.newRequest}</h1>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3, 4, 5].map(s => (
          <div
            key={s}
            className={`flex-1 h-2 rounded-full transition-colors ${s <= step ? 'bg-blue-600' : 'bg-slate-200'}`}
          />
        ))}
      </div>

      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-4 flex items-center gap-3" data-testid="form-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Step 1: Date */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">{t.fieldDate}</h2>
            <input
              type="date"
              value={formData.booking_date}
              onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="w-full p-4 border border-white/10 rounded-xl text-lg"
              data-testid="date-input"
            />
            <button
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
              className="w-full mt-6 bg-blue-600 text-white py-4 rounded-xl font-semibold disabled:opacity-50 hover:bg-blue-700 transition"
              data-testid="step1-continue"
            >
              {t.continua}
            </button>
          </div>
        )}

        {/* Step 2: Boat + Time Slot */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">{t.fieldBoat} & {t.fieldTimeSlot}</h2>
            <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
              {getAvailableBoats().map(boat => (
                <button
                  key={boat.id}
                  onClick={() => {
                    const slots = getAvailableSlots(boat);
                    const defaultSlot = slots.length === 1 ? slots[0].value : '';
                    const types = [];
                    if (boat.tour_gruppo) types.push('tour_gruppo');
                    if (boat.tour_esclusiva) types.push('tour_esclusiva');
                    if (boat.locazione) types.push('locazione');
                    setFormData({ ...formData, boat_id: boat.id, boat_name: boat.name, time_slot: defaultSlot, booking_type: types[0] || 'tour_gruppo' });
                  }}
                  className={`w-full p-4 rounded-xl border text-left transition ${
                    formData.boat_id === boat.id ? 'border-blue-600 bg-blue-50' : 'border-white/10 hover:bg-slate-800/50'
                  }`}
                  data-testid={`boat-select-${boat.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{boat.name}</p>
                      <p className="text-sm text-slate-500">{boat.model} · Max {boat.max_capacity} · {boat.departure_port}</p>
                    </div>
                    {boat.price_per_person > 0 && (
                      <span className="text-blue-600 font-semibold">€{boat.price_per_person}/pp</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {selectedBoat && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-600">{t.fieldTimeSlot}</label>
                <div className="flex flex-wrap gap-2">
                  {getAvailableSlots(selectedBoat).map(slot => (
                    <button
                      key={slot.value}
                      onClick={() => setFormData({ ...formData, time_slot: slot.value })}
                      className={`px-4 py-2.5 rounded-xl border font-medium text-sm transition ${
                        formData.time_slot === slot.value
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-white/10 text-slate-600 hover:bg-slate-800/50'
                      }`}
                      data-testid={`slot-${slot.value}`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="flex-1 py-4 border border-white/10 rounded-xl font-semibold hover:bg-slate-800/50 transition">
                {t.back}
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="flex-1 bg-blue-600 text-white py-4 rounded-xl font-semibold disabled:opacity-50 hover:bg-blue-700 transition"
                data-testid="step2-continue"
              >
                {t.continua}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Customer */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">{t.step3Title}</h2>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => { setIsNewCustomer(false); setFormData({ ...formData, customer_id: '', customer_display: '' }); }}
                className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition ${
                  !isNewCustomer ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}
                data-testid="existing-customer-tab"
              >
                {t.existingCustomer}
              </button>
              <button
                onClick={() => { setIsNewCustomer(true); setFormData({ ...formData, customer_id: '' }); }}
                className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition ${
                  isNewCustomer ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}
                data-testid="new-customer-tab"
              >
                {t.newCustomer}
              </button>
            </div>

            {!isNewCustomer ? (
              <div>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t.searchCustomer}
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-white/10 rounded-xl focus:border-blue-500 outline-none"
                    data-testid="customer-search"
                  />
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.id}
                      onClick={() => setFormData({ ...formData, customer_id: c.id, customer_display: `${c.first_name} ${c.last_name}` })}
                      className={`w-full p-3 rounded-xl border text-left transition ${
                        formData.customer_id === c.id ? 'border-blue-600 bg-blue-50' : 'border-white/10 hover:bg-slate-800/50'
                      }`}
                      data-testid={`customer-${c.id}`}
                    >
                      <p className="font-medium text-white">{c.first_name} {c.last_name}</p>
                      <p className="text-sm text-slate-500">{[c.phone, c.email].filter(Boolean).join(' · ') || t.noContact}</p>
                    </button>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <p className="text-center text-slate-500 py-4">{t.noCustomerFound}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">{t.fieldFirstName} *</label>
                    <input
                      type="text" value={formData.new_first_name}
                      onChange={(e) => setFormData({ ...formData, new_first_name: e.target.value })}
                      className="w-full p-3 border border-white/10 rounded-xl" placeholder="Mario"
                      data-testid="new-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">{t.fieldLastName} *</label>
                    <input
                      type="text" value={formData.new_last_name}
                      onChange={(e) => setFormData({ ...formData, new_last_name: e.target.value })}
                      className="w-full p-3 border border-white/10 rounded-xl" placeholder="Rossi"
                      data-testid="new-last-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">{t.fieldPhone}</label>
                  <input
                    type="tel" value={formData.new_phone}
                    onChange={(e) => setFormData({ ...formData, new_phone: e.target.value })}
                    className="w-full p-3 border border-white/10 rounded-xl" placeholder="+39 333 1234567"
                    data-testid="new-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1">{t.fieldEmail}</label>
                  <input
                    type="email" value={formData.new_email}
                    onChange={(e) => setFormData({ ...formData, new_email: e.target.value })}
                    className="w-full p-3 border border-white/10 rounded-xl" placeholder="mario@email.com"
                    data-testid="new-email"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(2)} className="flex-1 py-4 border border-white/10 rounded-xl font-semibold hover:bg-slate-800/50 transition">
                {t.back}
              </button>
              <button
                onClick={() => { setError(''); setStep(4); }}
                disabled={!canProceedStep3}
                className="flex-1 bg-blue-600 text-white py-4 rounded-xl font-semibold disabled:opacity-50 hover:bg-blue-700 transition"
                data-testid="step3-continue"
              >
                {t.continua}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Booking details */}
        {step === 4 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 text-white">{t.step4Title}</h2>
            <div className="space-y-4">

              {/* Booking type */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">{t.experienceType}</label>
                <div className="flex flex-wrap gap-2">
                  {getBookingTypes().map(t => (
                    <button key={t.value} onClick={() => setFormData({ ...formData, booking_type: t.value, skipper_included: false, end_date: '' })}
                      className={`px-4 py-2.5 rounded-xl border font-medium text-sm transition ${formData.booking_type === t.value ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 text-slate-400 hover:bg-slate-800/50'}`}
                      data-testid={`type-${t.value}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Multi-day end date — only for locazione + multi_day_enabled */}
              {formData.booking_type === 'locazione' && selectedBoat?.multi_day_enabled && (
                <div className="bg-slate-800/60 rounded-xl p-4 border border-white/10">
                  <label className="block text-sm font-medium text-slate-400 mb-2">{t.multiDayRental}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t.startDate}</label>
                      <div className="p-2.5 border border-white/10 rounded-lg text-sm text-white bg-slate-800">{formData.booking_date || '—'}</div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t.endDate}</label>
                      <input type="date" min={formData.booking_date || format(new Date(), 'yyyy-MM-dd')}
                        value={formData.end_date}
                        onChange={e => { setFormData({ ...formData, end_date: e.target.value }); setCustomTotalPrice(null); }}
                        className="w-full p-2.5 border border-white/10 rounded-lg text-sm text-white bg-slate-800 focus:border-blue-500 outline-none"
                      />
                    </div>
                  </div>
                  {durationDays > 1 && (
                    <p className="text-xs text-blue-400 mt-2">{t.duration} {durationDays} {durationDays === 1 ? t.dayLabel : t.daysLabel}</p>
                  )}
                  {selectedBoat.multi_day_min > 1 && durationDays < selectedBoat.multi_day_min && (
                    <p className="text-xs text-amber-400 mt-1">{t.minDaysRequired.replace('{n}', selectedBoat.multi_day_min)}</p>
                  )}
                </div>
              )}

              {/* Guest count */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">{t.numPeople}</label>
                <input type="number" min="1" max={selectedBoat?.max_capacity || 50}
                  value={formData.guest_count}
                  onChange={e => setFormData({ ...formData, guest_count: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-full p-3 border border-white/10 rounded-xl bg-slate-800 text-white focus:border-blue-500 outline-none"
                  data-testid="guest-count" />
                {selectedBoat && <p className="text-xs text-slate-500 mt-1">{t.maxPeople.replace('{n}', selectedBoat.max_capacity)}</p>}
              </div>

              {/* Skipper — only for locazione + skipper_available */}
              {formData.booking_type === 'locazione' && selectedBoat?.skipper_available && (
                <div className="bg-slate-800/60 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">
                        Skipper {selectedBoat.skipper_mode === 'mandatory' ? `(${t.skipperAlwaysIncluded.toLowerCase()})` : ''}
                      </p>
                      {selectedBoat.skipper_daily_price > 0 && (
                        <p className="text-xs text-slate-500">+€{selectedBoat.skipper_daily_price}/{t.dayLabel}</p>
                      )}
                    </div>
                    {selectedBoat.skipper_mode === 'mandatory' ? (
                      <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-lg">{t.skipperIncluded}</span>
                    ) : (
                      <button type="button"
                        onClick={() => { setFormData(f => ({ ...f, skipper_included: !f.skipper_included })); setCustomTotalPrice(null); }}
                        className={`w-12 h-6 rounded-full transition-colors relative ${formData.skipper_included ? 'bg-blue-600' : 'bg-slate-600'}`}>
                        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow ${formData.skipper_included ? 'translate-x-7' : 'translate-x-1'}`} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Booking source */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">{t.bookingSourceLabel}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'manual', label: t.sourceManual },
                    { value: 'phone', label: t.sourcePhone },
                    { value: 'whatsapp', label: t.sourceWhatsApp },
                    { value: 'website', label: t.sourceWebsite },
                    { value: 'agency', label: t.sourceAgency },
                  ].map(s => (
                    <button key={s.value} onClick={() => setFormData({ ...formData, booking_source: s.value })}
                      className={`px-3 py-2 rounded-xl border text-xs font-medium transition ${formData.booking_source === s.value ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 text-slate-400 hover:bg-slate-800/50'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment method */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">{t.paymentMethodLabel}</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'cash', label: t.pmCash },
                    { value: 'card', label: t.pmCard },
                    { value: 'bank_transfer', label: t.pmBankTransfer },
                    { value: 'payment_link', label: t.pmPaymentLink },
                    { value: 'other', label: t.pmOther },
                  ].map(m => (
                    <button key={m.value} onClick={() => setFormData({ ...formData, payment_method: m.value })}
                      className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition ${formData.payment_method === m.value ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-white/10 text-slate-400 hover:bg-slate-800/50'}`}
                      data-testid={`payment-${m.value}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">{t.customerNotesLabel}</label>
                <textarea value={formData.customer_notes} onChange={e => setFormData({ ...formData, customer_notes: e.target.value })}
                  className="w-full p-3 border border-white/10 rounded-xl bg-slate-800 text-white resize-none focus:border-blue-500 outline-none" rows={2}
                  placeholder={t.customerNotesLabel + '...'} data-testid="customer-notes" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">{t.internalNotesLabel}</label>
                <textarea value={formData.internal_notes} onChange={e => setFormData({ ...formData, internal_notes: e.target.value })}
                  className="w-full p-3 border border-white/10 rounded-xl bg-slate-800 text-white resize-none focus:border-blue-500 outline-none" rows={2}
                  placeholder={t.internalNotesPlaceholder + '...'} data-testid="internal-notes" />
              </div>

              {/* Price & Deposit */}
              <div className="bg-slate-800/60 rounded-xl p-4 border border-white/10">
                {loadingPrice && <p className="text-xs text-slate-500 mb-2">{t.loading}</p>}
                {seasonalPrice !== null && seasonalPrice !== unitPrice && (
                  <p className="text-xs text-emerald-400 mb-2">{t.suggestedPrice}</p>
                )}
                {skipperCost > 0 && (
                  <p className="text-xs text-slate-400 mb-2">{t.skipperIncluded}: +€{skipperCost} ({durationDays} {durationDays === 1 ? t.dayLabel : t.daysLabel} × €{selectedBoat?.skipper_daily_price})</p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">
                      {t.fieldTotal} (€){customTotalPrice === null && <span className="text-slate-500 ml-1">{t.suggestedPrice}</span>}
                    </label>
                    <input type="number" min="0" step="1"
                      value={customTotalPrice !== null ? customTotalPrice : suggestedPrice}
                      onChange={e => setCustomTotalPrice(parseFloat(e.target.value) || 0)}
                      className="w-full p-2.5 border border-white/10 rounded-lg bg-slate-700 text-white font-bold text-lg focus:border-blue-500 outline-none"
                      data-testid="total-price-input" />
                    {customTotalPrice !== null && customTotalPrice !== suggestedPrice && (
                      <button onClick={() => setCustomTotalPrice(null)} className="text-xs text-blue-400 hover:underline mt-1" data-testid="reset-price">
                        ↩ €{suggestedPrice.toLocaleString()}
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">{t.depositAmountLabel}</label>
                    <input type="number" min="0" step="1" value={customDeposit}
                      onChange={e => setCustomDeposit(e.target.value)}
                      placeholder="0"
                      className="w-full p-2.5 border border-white/10 rounded-lg bg-slate-700 text-white font-bold text-lg focus:border-blue-500 outline-none placeholder:text-slate-600 placeholder:font-normal"
                      data-testid="deposit-input" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(3)} className="flex-1 py-4 border border-white/10 rounded-xl font-semibold text-white hover:bg-slate-800/50 transition">{t.back}</button>
              <button onClick={() => { setError(''); setStep(5); }} disabled={!canProceedStep4}
                className="flex-1 bg-blue-600 text-white py-4 rounded-xl font-semibold disabled:opacity-50 hover:bg-blue-700 transition"
                data-testid="step4-continue">
                {t.continua}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Confirm */}
        {step === 5 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">{t.confirmBooking}</h2>
            <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">{t.fieldDate}</span>
                <span className="font-medium">{formData.booking_date ? format(parseISO(formData.booking_date), 'd MMMM yyyy', { locale: it }) : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.fieldTimeSlot}</span>
                <span className="font-medium">
                  {formData.time_slot === 'full_day' ? t.fullDay : formData.time_slot === 'morning' ? t.morning : formData.time_slot === 'afternoon' ? t.afternoon : formData.time_slot === 'sunset' ? t.sunset : formData.time_slot}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.fieldBoat}</span>
                <span className="font-medium">{formData.boat_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.guestFallback}</span>
                <span className="font-medium">{isNewCustomer ? `${formData.new_first_name} ${formData.new_last_name}` : formData.customer_display}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.paymentType}</span>
                <span className="font-medium">
                  {formData.booking_type === 'tour_gruppo' ? t.typeGroup : formData.booking_type === 'tour_esclusiva' ? t.typeExclusive : t.typeRental}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.fieldGuests}</span>
                <span className="font-medium">{formData.guest_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t.paymentMethod}</span>
                <span className="font-medium capitalize">{formData.payment_method}</span>
              </div>
              <div className="border-t border-white/10 pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-white">{t.fieldTotal}</span>
                  <span className="font-bold text-lg text-white">€{finalTotalPrice.toLocaleString()}</span>
                </div>
                {depositAmount > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-500">{t.fieldDeposit}</span>
                    <span className="text-blue-700 font-medium">€{depositAmount.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(4)} className="flex-1 py-4 border border-white/10 rounded-xl font-semibold hover:bg-slate-800/50 transition">
                {t.back}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-booking"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t.bookingCreating}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    {t.confirmBooking}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PUBLIC BOOKING PAGE ====================
const BOOKING_HERO = "https://nautiqo.cloud/placeholder.jpg";
const STOCK_BOATS = [
  "https://images.unsplash.com/photo-1648997934392-7213a9ce50b7?w=600&q=80",
  "https://images.unsplash.com/photo-1627761801957-4bf6cfb4fa20?w=600&q=80",
  "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=600&q=80",
  "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80",
];

// Static enrichment for operator's boats (public API returns minimal data)
const BOAT_ENRICHMENT = {
  'ed0b1c97-fcf0-4a08-abfa-b3e1f2e12c86': { model: 'Altamarea', port: 'Marsala', pp: 80, fd: 350, hd: 0, sunset: 0, dep: 30, gruppo: true, esclusiva: true, locazione: true, slots: { full_day: true }, img: STOCK_BOATS[0] },
  '3fd66345-8c9f-4e9c-8b95-8e3a361e5e66': { model: 'Raffaelli', port: 'Marsala', pp: 100, fd: 150, hd: 0, sunset: 0, dep: 30, gruppo: true, esclusiva: true, locazione: false, slots: { full_day: true }, img: STOCK_BOATS[1] },
  'd8d72f71-ca9f-4627-a24d-1e6e2a558c62': { model: 'Altamarea', port: 'Marsala', pp: 0, fd: 150, hd: 0, sunset: 0, dep: 30, gruppo: false, esclusiva: false, locazione: true, slots: { full_day: true }, img: STOCK_BOATS[2] },
  '3304a0e1-5bfc-4554-93b3-ee7a2da7885f': { model: 'Altamarea Wave 27', port: 'Marsala', pp: 80, fd: 0, hd: 0, sunset: 0, dep: 30, gruppo: true, esclusiva: true, locazione: false, slots: { morning: true, full_day: true }, img: STOCK_BOATS[3] },
};

const OPERATOR_BOATS = (process.env.REACT_APP_OPERATOR_BOATS || '').split(',').filter(Boolean);

const PublicBookingPage = () => {
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    booking_date: '',
    time_slot: '',
    guest_count: 1,
    booking_type: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    axios.get(`${API}/public/boats`)
      .then(res => {
        const allBoats = res.data || [];
        // Filter to operator's boats and enrich with static data
        const enriched = allBoats
          .filter(b => OPERATOR_BOATS.length === 0 || OPERATOR_BOATS.some(id => b.id.startsWith(id.trim().substring(0, 8))))
          .map(b => {
            const e = Object.entries(BOAT_ENRICHMENT).find(([key]) => b.id.startsWith(key.substring(0, 8)));
            if (e) {
              const [, data] = e;
              return { ...b, model: data.model, departure_port: data.port, price_per_person: data.pp, full_day_price: data.fd, half_day_price: data.hd, sunset_price: data.sunset, deposit_percent: data.dep, tour_gruppo: data.gruppo, tour_esclusiva: data.esclusiva, locazione: data.locazione, enabled_slots: data.slots, photo: data.img };
            }
            return b;
          });
        setBoats(enriched);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getSlots = (boat) => {
    if (!boat?.enabled_slots) return [{ value: 'full_day', label: 'Giornata intera' }];
    const labels = { morning: 'Mattina', afternoon: 'Pomeriggio', full_day: 'Giornata intera', sunset: 'Tramonto' };
    const slots = Object.entries(boat.enabled_slots).filter(([, v]) => v).map(([k]) => ({ value: k, label: labels[k] || k }));
    return slots.length > 0 ? slots : [{ value: 'full_day', label: 'Giornata intera' }];
  };

  const getTypes = (boat) => {
    if (!boat) return [];
    const t = [];
    if (boat.tour_gruppo) t.push({ value: 'tour_gruppo', label: 'Tour in gruppo', desc: 'Condividi l\'esperienza con altri ospiti' });
    if (boat.tour_esclusiva) t.push({ value: 'tour_esclusiva', label: 'Tour esclusiva', desc: 'La barca tutta per te e il tuo gruppo' });
    if (boat.locazione) t.push({ value: 'locazione', label: 'Locazione', desc: 'Noleggio senza equipaggio' });
    return t.length > 0 ? t : [{ value: 'tour_gruppo', label: 'Escursione', desc: '' }];
  };

  const calcPrice = () => {
    if (!selectedBoat) return 0;
    const { booking_type, guest_count, time_slot } = form;
    if (booking_type === 'tour_gruppo' && selectedBoat.price_per_person > 0) return selectedBoat.price_per_person * (parseInt(guest_count) || 1);
    if (booking_type === 'tour_esclusiva') {
      if (time_slot === 'full_day') return selectedBoat.full_day_price || 0;
      if (time_slot === 'morning' || time_slot === 'afternoon') return selectedBoat.half_day_price || 0;
      if (time_slot === 'sunset') return selectedBoat.sunset_price || 0;
    }
    if (booking_type === 'locazione') {
      if (time_slot === 'full_day') return selectedBoat.full_day_price || 0;
      if (time_slot === 'morning' || time_slot === 'afternoon') return selectedBoat.half_day_price || 0;
    }
    return selectedBoat.full_day_price || 0;
  };

  const price = calcPrice();
  const deposit = Math.round(price * (selectedBoat?.deposit_percent || 30) / 100);

  const openBoat = (boat) => {
    const types = getTypes(boat);
    const slots = getSlots(boat);
    setSelectedBoat(boat);
    setForm({ booking_date: '', time_slot: slots.length === 1 ? slots[0].value : '', guest_count: 1, booking_type: types[0]?.value || '', first_name: '', last_name: '', email: '', phone: '', notes: '' });
    setBookingStep(1);
    setSuccess(false);
    setError('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/public/booking-request`, {
        boat_id: selectedBoat.id,
        boat_name: selectedBoat.name,
        booking_date: form.booking_date,
        time_slot: form.time_slot,
        guest_count: parseInt(form.guest_count),
        booking_type: form.booking_type,
        customer_name: `${form.first_name} ${form.last_name}`,
        customer_email: form.email,
        customer_phone: form.phone,
        customer_notes: form.notes,
        total_price: price,
      }).catch(() => {
        // The public booking-request endpoint may not exist yet on the external backend
        // Show success anyway - the operator will see it as a contact request
      });
      setSuccess(true);
    } catch (err) {
      setError('Si e verificato un errore. Riprova o contattaci direttamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = form.booking_date && form.time_slot && form.booking_type;
  const canProceedStep2 = form.guest_count > 0;
  const canSubmit = form.first_name && form.last_name && (form.email || form.phone);

  const slotLabel = (v) => ({ morning: 'Mattina', afternoon: 'Pomeriggio', full_day: 'Giornata intera', sunset: 'Tramonto' }[v] || v);
  const typeLabel = (v) => ({ tour_gruppo: 'Tour in gruppo', tour_esclusiva: 'Tour esclusiva', locazione: 'Locazione' }[v] || v);

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-slate-800/50" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/10 backdrop-blur-sm/80 border-b border-white/10/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Link to="/landing" className="flex items-center gap-2" data-testid="book-logo">
            <div className="w-8 h-8 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Nautiqo</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-500 hover:text-white font-medium transition-colors">Sei un operatore?</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-14" data-testid="book-hero">
        <div className="relative h-64 sm:h-80 overflow-hidden">
          <img src={BOOKING_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-black/30 to-black/20" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Prenota la tua <span className="font-semibold">esperienza in mare</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-lg">
              Scegli la barca, la data e salpa. E' semplice.
            </p>
          </div>
        </div>
      </section>

      {/* Boat Grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 -mt-8 relative z-10 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="boat-grid">
          {boats.map((boat, idx) => (
            <button
              key={boat.id || idx}
              onClick={() => openBoat(boat)}
              className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left group"
              data-testid={`public-boat-${boat.id}`}
            >
              <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-50 overflow-hidden relative">
                <img
                  src={boat.photo || STOCK_BOATS[idx % STOCK_BOATS.length]}
                  alt={boat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {boat.departure_port && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-1 text-xs text-white bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
                    <MapPin className="w-3 h-3" /> {boat.departure_port}
                  </div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-lg text-white">{boat.name}</h3>
                    <p className="text-sm text-slate-400">{boat.model || 'Barca a motore'}</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Users className="w-4 h-4" /> {boat.max_capacity}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {boat.tour_gruppo && <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-semibold">Gruppo</span>}
                  {boat.tour_esclusiva && <span className="px-2 py-0.5 bg-purple-50 text-purple-600 rounded text-xs font-semibold">Esclusiva</span>}
                  {boat.locazione && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs font-semibold">Locazione</span>}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <div>
                    {boat.price_per_person > 0 && (
                      <><span className="text-xs text-slate-400">Da </span><span className="text-xl font-bold text-white">€{boat.price_per_person}</span><span className="text-slate-400 text-sm">/pp</span></>
                    )}
                    {!boat.price_per_person && boat.full_day_price > 0 && (
                      <><span className="text-xs text-slate-400">Da </span><span className="text-xl font-bold text-white">€{boat.full_day_price}</span><span className="text-slate-400 text-sm">/giorno</span></>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-blue-600 group-hover:text-blue-700 flex items-center gap-1 transition-colors">
                    Prenota <ChevronRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {boats.length === 0 && !loading && (
          <div className="text-center py-20">
            <Ship className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Nessuna barca disponibile</h2>
            <p className="text-slate-500">Riprova piu tardi o contattaci direttamente.</p>
          </div>
        )}
      </main>

      {/* ===== BOOKING MODAL ===== */}
      {selectedBoat && !success && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setSelectedBoat(null)}>
          <div className="bg-white/10 backdrop-blur-sm w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="booking-modal">
            {/* Boat header */}
            <div className="relative h-44 sm:h-52 overflow-hidden sm:rounded-t-2xl">
              <img src={selectedBoat.photo || STOCK_BOATS[0]} alt={selectedBoat.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <button onClick={() => setSelectedBoat(null)} className="absolute top-4 right-4 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" data-testid="close-booking-modal">
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-5 right-5">
                <h2 className="text-white font-bold text-xl mb-1">{selectedBoat.name}</h2>
                <div className="flex items-center gap-3 text-white/70 text-sm">
                  {selectedBoat.departure_port && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {selectedBoat.departure_port}</span>}
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Max {selectedBoat.max_capacity}</span>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="flex gap-2 px-5 pt-4">
              {[1, 2, 3].map(s => (
                <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${s <= bookingStep ? 'bg-blue-600' : 'bg-slate-200'}`} />
              ))}
            </div>

            <div className="p-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-sm mb-4 flex items-center gap-2" data-testid="booking-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                </div>
              )}

              {/* Step 1: Date, Time, Type */}
              {bookingStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Quando vuoi partire?</label>
                    <input type="date" value={form.booking_date}
                      onChange={e => setForm({ ...form, booking_date: e.target.value })}
                      min={format(new Date(), 'yyyy-MM-dd')}
                      className="w-full p-3.5 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none text-white"
                      data-testid="public-date-input" />
                  </div>

                  {getSlots(selectedBoat).length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">Fascia oraria</label>
                      <div className="grid grid-cols-2 gap-2">
                        {getSlots(selectedBoat).map(s => (
                          <button key={s.value} onClick={() => setForm({ ...form, time_slot: s.value })}
                            className={`p-3 rounded-xl border text-sm font-medium transition ${
                              form.time_slot === s.value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-white/10 text-slate-600 hover:bg-slate-800/50'
                            }`} data-testid={`public-slot-${s.value}`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {getTypes(selectedBoat).length > 1 && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">Tipo di esperienza</label>
                      <div className="space-y-2">
                        {getTypes(selectedBoat).map(t => (
                          <button key={t.value} onClick={() => setForm({ ...form, booking_type: t.value })}
                            className={`w-full p-3.5 rounded-xl border text-left transition ${
                              form.booking_type === t.value ? 'border-blue-600 bg-blue-50' : 'border-white/10 hover:bg-slate-800/50'
                            }`} data-testid={`public-type-${t.value}`}>
                            <span className="font-medium text-white text-sm">{t.label}</span>
                            <span className="text-xs text-slate-500 block mt-0.5">{t.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button onClick={() => { setError(''); setBookingStep(2); }} disabled={!canProceedStep1}
                    className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-40 hover:bg-blue-700 transition"
                    data-testid="public-step1-continue">
                    Continua
                  </button>
                </div>
              )}

              {/* Step 2: Guests */}
              {bookingStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Quante persone?</label>
                    <div className="flex items-center gap-4">
                      <button onClick={() => setForm({ ...form, guest_count: Math.max(1, form.guest_count - 1) })}
                        className="w-12 h-12 border border-white/10 rounded-xl flex items-center justify-center text-lg font-bold text-slate-600 hover:bg-slate-800/50 transition" data-testid="guests-minus">
                        -
                      </button>
                      <span className="text-3xl font-bold text-white w-16 text-center" data-testid="guest-count-display">{form.guest_count}</span>
                      <button onClick={() => setForm({ ...form, guest_count: Math.min(selectedBoat.max_capacity || 50, form.guest_count + 1) })}
                        className="w-12 h-12 border border-white/10 rounded-xl flex items-center justify-center text-lg font-bold text-slate-600 hover:bg-slate-800/50 transition" data-testid="guests-plus">
                        +
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Massimo {selectedBoat.max_capacity} persone per questa barca</p>
                  </div>

                  {/* Price preview */}
                  {price > 0 && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Prezzo stimato</span>
                        <span className="text-2xl font-bold text-white">€{price.toLocaleString()}</span>
                      </div>
                      {form.booking_type === 'tour_gruppo' && selectedBoat.price_per_person > 0 && (
                        <p className="text-xs text-slate-500 mt-1">€{selectedBoat.price_per_person}/persona × {form.guest_count} persone</p>
                      )}
                      {deposit > 0 && (
                        <p className="text-xs text-blue-600 mt-1 font-medium">Acconto: €{deposit.toLocaleString()} ({selectedBoat.deposit_percent || 30}%)</p>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button onClick={() => setBookingStep(1)} className="flex-1 py-3.5 border border-white/10 rounded-xl font-semibold text-slate-600 hover:bg-slate-800/50 transition">
                      Indietro
                    </button>
                    <button onClick={() => { setError(''); setBookingStep(3); }} disabled={!canProceedStep2}
                      className="flex-1 py-3.5 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-40 hover:bg-blue-700 transition"
                      data-testid="public-step2-continue">
                      Continua
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Contact Info */}
              {bookingStep === 3 && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500 mb-2">Inserisci i tuoi dati per completare la prenotazione.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Nome *</label>
                      <input type="text" value={form.first_name}
                        onChange={e => setForm({ ...form, first_name: e.target.value })}
                        className="w-full p-3 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" placeholder="Mario"
                        data-testid="public-first-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Cognome *</label>
                      <input type="text" value={form.last_name}
                        onChange={e => setForm({ ...form, last_name: e.target.value })}
                        className="w-full p-3 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" placeholder="Rossi"
                        data-testid="public-last-name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
                    <input type="email" value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="w-full p-3 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" placeholder="mario@email.com"
                      data-testid="public-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Telefono</label>
                    <input type="tel" value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full p-3 border border-white/10 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" placeholder="+39 333 1234567"
                      data-testid="public-phone" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Note (opzionale)</label>
                    <textarea value={form.notes}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      className="w-full p-3 border border-white/10 rounded-xl resize-none focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" rows={2} placeholder="Richieste particolari, allergie, bambini..."
                      data-testid="public-notes" />
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-800/50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Barca</span><span className="font-medium text-white">{selectedBoat.name}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Data</span><span className="font-medium text-white">{form.booking_date ? format(parseISO(form.booking_date), 'd MMMM yyyy', { locale: it }) : '-'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Orario</span><span className="font-medium text-white">{slotLabel(form.time_slot)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Tipo</span><span className="font-medium text-white">{typeLabel(form.booking_type)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Persone</span><span className="font-medium text-white">{form.guest_count}</span></div>
                    {price > 0 && (
                      <div className="flex justify-between pt-2 border-t border-white/10">
                        <span className="font-semibold text-white">Totale</span>
                        <span className="font-bold text-lg text-white">€{price.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setBookingStep(2)} className="flex-1 py-3.5 border border-white/10 rounded-xl font-semibold text-slate-600 hover:bg-slate-800/50 transition">
                      Indietro
                    </button>
                    <button onClick={handleSubmit} disabled={!canSubmit || submitting}
                      className="flex-1 py-3.5 bg-emerald-600 text-white rounded-xl font-semibold disabled:opacity-40 hover:bg-emerald-700 transition flex items-center justify-center gap-2"
                      data-testid="public-submit-booking">
                      {submitting ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Invio...</>
                      ) : (
                        <><Check className="w-5 h-5" />Invia richiesta</>
                      )}
                    </button>
                  </div>

                  <p className="text-xs text-slate-400 text-center">
                    La tua richiesta verra confermata dall'operatore. Non verra addebitato nulla ora.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== SUCCESS SCREEN ===== */}
      {success && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl w-full max-w-md p-8 text-center" data-testid="booking-success">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Richiesta inviata!</h2>
            <p className="text-slate-500 mb-2">
              La tua richiesta per <strong>{selectedBoat?.name}</strong> il{' '}
              <strong>{form.booking_date ? format(parseISO(form.booking_date), 'd MMMM yyyy', { locale: it }) : ''}</strong> è stata inviata.
            </p>
            <p className="text-sm text-slate-400 mb-8">
              Riceverai una conferma appena l'operatore approvera la tua prenotazione.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setSelectedBoat(null); setSuccess(false); }}
                className="flex-1 py-3 border border-white/10 rounded-xl font-semibold text-slate-600 hover:bg-slate-800/50 transition"
                data-testid="success-browse-more">
                Altre barche
              </button>
              <Link to="/landing"
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition text-center"
                data-testid="success-home">
                Torna alla home
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white/10 backdrop-blur-sm border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-[22%] overflow-hidden flex-shrink-0"><img src={LOGO_URL} alt="Nautiqo" className="w-full h-full scale-[1.06]" /></div>
            <span className="font-bold text-sm text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Nautiqo</span>
          </div>
          <p className="text-xs text-slate-400">Prenotazione sicura e semplice. Powered by Nautiqo.</p>
        </div>
      </footer>
    </div>
  );
};

// v2
// ==================== SLUG BOOKING PAGE (REDIRECT) ====================
// The public booking page is rendered server-side as full HTML on the
// backend domain (api.nautiqo.cloud) at `/<slug>`. Trying to fetch that
// HTML from the React app and inject it into the DOM was unreliable
// (rendered raw JSON when the JSON endpoint was hit by mistake, and
// shipped scripts cross-origin). Instead, simply redirect the browser.
const SLUG_REDIRECT_BASE = 'https://api.nautiqo.cloud';
const SLUG_FORMAT_RE = /^[a-z0-9-]+$/i;

const SlugBookingPage = () => {
  const { slug } = useParams();
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!slug || !SLUG_FORMAT_RE.test(slug)) {
      setInvalid(true);
      return;
    }
    // Hand off to the backend's HTML SSR page. Use replace() so the
    // /booking/<slug> URL is not kept in browser history.
    window.location.replace(`${SLUG_REDIRECT_BASE}/${slug}`);
  }, [slug]);

  if (invalid) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0A2540', color: '#E2E8F0', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ background: '#0F2A42', borderRadius: 20, padding: 40, textAlign: 'center', maxWidth: 400 }}>
          <h1 style={{ fontSize: 24, marginBottom: 12, color: '#FF6B6B' }}>Link non valido</h1>
          <p style={{ color: '#8899AA', lineHeight: 1.6 }}>Questo link di prenotazione non è più attivo o non esiste.</p>
        </div>
      </div>
    );
  }

  // Brief redirect splash — usually invisible because the redirect fires
  // synchronously in the effect on the next paint.
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F6FB' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #E2E8F0', borderTopColor: '#0052CC', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#5A6A7E', fontSize: 14 }}>Caricamento...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );
};

// ==================== SUPER ADMIN WRAPPER ====================
const SuperAdminWrapper = ({ page }) => {
  const { user, logout, getAuthHeaders } = useAuth();
  const pages = {
    dashboard: <SADashboardPage getAuthHeaders={getAuthHeaders} />,
    insights: <SAInsightsPage getAuthHeaders={getAuthHeaders} />,
    revenue: <SARevenuePage getAuthHeaders={getAuthHeaders} />,
    aziende: <SAAziendePage getAuthHeaders={getAuthHeaders} />,
    notifiche: <SANotifichePage getAuthHeaders={getAuthHeaders} />,
    marketing: <SAMarketingPage getAuthHeaders={getAuthHeaders} />,
    whatsapp: <SAWhatsAppPage getAuthHeaders={getAuthHeaders} />,
    lead: <SALeadsPage getAuthHeaders={getAuthHeaders} />,
    supporto: <SASupportoPage getAuthHeaders={getAuthHeaders} />,
    rimborsi: <SARimborsiPage getAuthHeaders={getAuthHeaders} />,
    utenti: <SAUtentiPage getAuthHeaders={getAuthHeaders} />,
    coupon: <SACouponsPage getAuthHeaders={getAuthHeaders} />,
  };
  return (
    <SuperAdminLayout user={user} logout={logout} getAuthHeaders={getAuthHeaders}>
      {pages[page]}
    </SuperAdminLayout>
  );
};

const SACompanyDetailWrapper = () => {
  const { id } = useParams();
  const { user, logout, getAuthHeaders } = useAuth();
  return (
    <SuperAdminLayout user={user} logout={logout} getAuthHeaders={getAuthHeaders}>
      <SACompanyDetailPage id={id} getAuthHeaders={getAuthHeaders} />
    </SuperAdminLayout>
  );
};

// ==================== MAIN APP ====================
function App() {
  useEffect(() => {
    document.title = 'Nautiqo \u2014 Gestisci il tuo charter';
  }, []);

  return (
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/booking/:slug" element={<SlugBookingPage />} />
          <Route path="/book" element={<PublicBookingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/download" element={<DownloadRedirect />} />
          
          <Route path="/admin" element={<ProtectedRoute><AdminLayout><DashboardPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/prenotazioni" element={<ProtectedRoute><AdminLayout><PrenotazioniPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/nuova-prenotazione" element={<ProtectedRoute><AdminLayout><NuovaPrenotazionePage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/barche" element={<ProtectedRoute><AdminLayout><BarchePage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/fatturato" element={<ProtectedRoute><AdminLayout><FatturatoPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/impostazioni" element={<ProtectedRoute><AdminLayout><ProfiloPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/account" element={<ProtectedRoute><AdminLayout><AccountPage /></AdminLayout></ProtectedRoute>} />
          <Route path="/account" element={<AccountDeepLinkRoute />} />
          <Route path="/subscription" element={<SubscriptionRedirect />} />
          <Route path="/pricing" element={<PricingPage />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminWrapper page="dashboard" /></SuperAdminRoute>} />
          <Route path="/super-admin/insights" element={<SuperAdminRoute><SuperAdminWrapper page="insights" /></SuperAdminRoute>} />
          <Route path="/super-admin/revenue" element={<SuperAdminRoute><SuperAdminWrapper page="revenue" /></SuperAdminRoute>} />
          <Route path="/super-admin/aziende" element={<SuperAdminRoute><SuperAdminWrapper page="aziende" /></SuperAdminRoute>} />
          <Route path="/super-admin/aziende/:id" element={<SuperAdminRoute><SACompanyDetailWrapper /></SuperAdminRoute>} />
          <Route path="/super-admin/notifiche" element={<SuperAdminRoute><SuperAdminWrapper page="notifiche" /></SuperAdminRoute>} />
          <Route path="/super-admin/marketing" element={<SuperAdminRoute><SuperAdminWrapper page="marketing" /></SuperAdminRoute>} />
          <Route path="/super-admin/whatsapp" element={<SuperAdminRoute><SuperAdminWrapper page="whatsapp" /></SuperAdminRoute>} />
          <Route path="/super-admin/lead" element={<SuperAdminRoute><SuperAdminWrapper page="lead" /></SuperAdminRoute>} />
          <Route path="/super-admin/supporto" element={<SuperAdminRoute><SuperAdminWrapper page="supporto" /></SuperAdminRoute>} />
          <Route path="/super-admin/rimborsi" element={<SuperAdminRoute><SuperAdminWrapper page="rimborsi" /></SuperAdminRoute>} />
          <Route path="/super-admin/utenti" element={<SuperAdminRoute><SuperAdminWrapper page="utenti" /></SuperAdminRoute>} />
          <Route path="/super-admin/coupon" element={<SuperAdminRoute><SuperAdminWrapper page="coupon" /></SuperAdminRoute>} />

          <Route path="/prova-gratis" element={<LeadPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <SonnerToaster
          position="bottom-right"
          theme="dark"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast: "!bg-slate-900/95 !backdrop-blur-xl !border !border-white/10 !text-white !shadow-2xl !rounded-2xl",
              title: "!text-white !font-semibold",
              description: "!text-slate-400 !text-xs",
              success: "!border-emerald-500/30",
              error: "!border-red-500/30",
            },
          }}
        />
      </BrowserRouter>
      <Analytics />
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
